import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  "fr": {
    "translation": {
      "nav": {
        "features": "Fonctionnalités",
        "howItWorks": "Comment ça marche",
        "pricing": "Tarifs",
        "contact": "Contact",
        "login": "Connexion",
        "home": "Accueil",
        "register": "Enregistrer"
      },
      "hero": {
        "badge": "Plateforme SaaS · Maintenance digitale par QR Code",
        "title": "Digitalisez vos interventions techniques avec un simple scan",
        "subtitle": "Chaque machine reliée à un QR code, chaque intervention tracée, chaque rapport généré automatiquement. Une plateforme web + mobile pour centraliser et automatiser toute votre maintenance.",
        "login": "Se connecter",
        "signup": "Créer un compte",
        "discover": "Decouvrir"
      },
      "pricing": {
        "label": "Tarification",
        "title": "Essayez gratuitement, payez seulement si vous êtes convaincu",
        "subtitle": "Essai gratuit pour démarrer, puis activation Pro par carte en mode démo quand vous êtes prêt.",
        "noCreditCard": "Paiement carte en mode démo",
        "noCommitment": "Sans engagement",
        "cancelAnytime": "Annulable à tout moment",
        "payment": {
          "demoBadge": "Paiement carte démo",
          "title": "Activez votre abonnement Pro",
          "subtitle": "Finalisez l’activation avec une carte de test. Aucun débit réel n’est effectué dans ce mode.",
          "cardName": "Nom sur la carte",
          "cardNamePlaceholder": "Marie Dupont",
          "cardNumber": "Numéro de carte",
          "expiry": "Expiration",
          "cvc": "CVC",
          "demoNotice": "Mode paiement non actif : cette validation simule le paiement et active l’abonnement pour votre application.",
          "mockOnly": "Transaction simulée",
          "noCharge": "Aucun débit bancaire",
          "activateNow": "Activation immédiate",
          "missingApp": "Connectez-vous avec votre application avant d’activer un abonnement.",
          "invalid": "Veuillez compléter les informations de carte de test.",
          "processing": "Activation…",
          "payButton": "Activer l’abonnement",
          "success": "Abonnement activé. Vous pouvez maintenant générer plus de QR codes.",
          "backToQr": "Retour au générateur QR"
        },
        "plans": [
          {
            "name": "Essai gratuit",
            "price": "€0",
            "period": "pendant 14 jours",
            "description": "Testez toutes les fonctionnalités sans engagement ni carte bancaire.",
            "badge": "14 jours gratuits",
            "cta": "Commencer l'essai",
            "features": [
              "Accès complet à la plateforme",
              "5 QR codes inclus",
              "Jusqu’à 5 utilisateurs",
              "Formulaires dynamiques",
              "Rapports PDF",
              "Support email"
            ]
          },
          {
            "name": "Pro",
            "price": "€149",
            "period": "/mois",
            "description": "Après votre essai, passez à Pro pour continuer sans interruption.",
            "badge": "",
            "cta": "Choisir l’offre Pro",
            "features": [
              "Tout ce qui est inclus dans l’essai",
              "Utilisateurs illimités",
              "Planification avancée",
              "Notifications en temps réel",
              "Accès API",
              "Support prioritaire",
              "Export PDF avancé"
            ]
          },
          {
            "name": "Entreprise",
            "price": "Personnalisé",
            "period": "",
            "description": "Pour les grandes organisations avec des besoins spécifiques et multi-sites.",
            "badge": "",
            "cta": "Nous contacter",
            "features": [
              "Tout ce qui est inclus dans Pro",
              "SSO & SAML",
              "SLA garanti",
              "Responsable dédié",
              "Déploiement sur site",
              "Formation sur site"
            ]
          }
        ]
      },
      "features": {
        "badge": "Fonctionnalités",
        "title": "Tout ce qu'il faut pour digitaliser votre maintenance",
        "subtitle": "Une plateforme complète qui couvre chaque étape, du scan QR à l'export du rapport final.",
        "qrcodes": {
          "title": "QR Codes intelligents",
          "desc": "Chaque machine est reliée à un QR code unique. Un simple scan ouvre le formulaire adapté à l'équipement et au contexte d'intervention."
        },
        "forms": {
          "title": "Formulaires dynamiques",
          "desc": "Créez des questionnaires hiérarchisés (titres, sous-titres, questions) avec réponses libres, choix multiples ou oui/non, adaptés à chaque type d'intervention."
        },
        "planning": {
          "title": "Planification intelligente",
          "desc": "Le système propose automatiquement des créneaux disponibles en fonction des horaires, jours ouvrés et jours fériés de chaque technicien."
        },
        "reports": {
          "title": "Rapports automatisés",
          "desc": "À chaque intervention terminée, un rapport structuré est généré avec l'ensemble des données collectées, consultable et exportable en PDF."
        },
        "notifications": {
          "title": "Notifications en temps réel",
          "desc": "Les administrateurs, techniciens et clients sont notifiés à chaque étape : nouvelle demande, assignation, intervention terminée."
        },
        "roles": {
          "title": "Gestion multi-rôles",
          "desc": "Administrez votre équipe avec des rôles distincts (admin, technicien, client) et des permissions adaptées à chaque profil."
        },
        "mobile": {
          "title": "Application mobile terrain",
          "desc": "Les techniciens et utilisateurs travaillent depuis l'app Android : scan, diagnostic, saisie et soumission directement sur le terrain."
        },
        "languages": {
          "title": "Support multilingue",
          "desc": "L'interface et les messages s'adaptent dynamiquement à la langue de chaque utilisateur grâce à un système de traduction côté serveur."
        },
        "history": {
          "title": "Historique & filtrage avancé",
          "desc": "Retrouvez n'importe quelle intervention passée grâce aux filtres par utilisateur, machine, technicien, date et statut."
        }
      },
      "steps": {
        "badge": "Comment ça marche",
        "title": "Du scan au rapport en 5 étapes",
        "subtitle": "Un processus fluide et automatisé, de la configuration initiale jusqu'à la génération du rapport final.",
        "configure": {
          "title": "Configurez vos machines et formulaires",
          "desc": "Depuis l'interface web, l'administrateur définit les types de machines et crée des formulaires hiérarchisés avec des questions adaptées à chaque contexte d'intervention."
        },
        "qrcode": {
          "title": "Générez et déployez vos QR codes",
          "desc": "Chaque équipement reçoit un QR code unique. Imprimez-les et placez-les sur vos machines pour les rendre instantanément accessibles."
        },
        "scan": {
          "title": "Scan, diagnostic et soumission",
          "desc": "L'utilisateur scanne le QR code depuis l'app mobile, remplit le formulaire adapté (diagnostic, demande directe) et soumet sa requête en quelques secondes."
        },
        "planning": {
          "title": "Planification et assignation automatique",
          "desc": "Le système identifie les techniciens disponibles selon leurs horaires, compétences et contraintes, puis propose automatiquement un créneau d'intervention."
        },
        "report": {
          "title": "Intervention et rapport généré",
          "desc": "Le technicien intervient, complète les données terrain. Un rapport structuré est automatiquement généré, consultable en ligne et exportable en PDF."
        }
      },
      "showcase": {
        "badge": "Produit",
        "title": "Web + Mobile, une seule plateforme",
        "subtitle": "L'administrateur configure et supervise depuis le tableau de bord web. Les techniciens et utilisateurs scannent, diagnostiquent et interviennent depuis l'application mobile Android."
      },
      "benefits": {
        "badge": "Avantages",
        "title": "Des résultats concrets dès le premier mois",
        "time": {
          "title": "Gagnez du temps",
          "desc": "Plus besoin de chercher les historiques ou de remplir des formulaires papier. Chaque intervention est tracée automatiquement du scan au rapport.",
          "stat": "temps de traitement"
        },
        "planning": {
          "title": "Interventions mieux planifiées",
          "desc": "Le moteur de planification intelligent prend en compte les disponibilités réelles, les jours fériés et les compétences de chaque technicien.",
          "stat": "plus d'interventions/jour"
        },
        "paperless": {
          "title": "Zéro papier, zéro perte",
          "desc": "Les formulaires dynamiques remplacent les carnets. Toutes les données sont structurées, stockées et accessibles à tout moment depuis n'importe quel appareil.",
          "stat": "document papier"
        },
        "data": {
          "title": "Données centralisées",
          "desc": "Historique complet des interventions, rapports exportables en PDF, filtrage avancé par technicien, machine, date ou statut — tout au même endroit.",
          "stat": "de traçabilité"
        }
      },
      "cases": {
        "badge": "Cas d'usage",
        "title": "Adapté à votre secteur",
        "subtitle": "Quel que soit votre domaine, AssistByScan s'adapte à vos processus de maintenance.",
        "industry": {
          "title": "Industrie",
          "desc": "Maintenance préventive et curative sur lignes de production. Chaque machine identifiée par QR code, chaque intervention tracée."
        },
        "services": {
          "title": "Services de maintenance",
          "desc": "Gestion multi-clients et multi-sites. Planifiez, assignez et suivez les interventions de vos techniciens en temps réel."
        },
        "retail": {
          "title": "Retail & Commerce",
          "desc": "Suivi des équipements en magasin (climatisation, caisses, éclairage) avec des demandes d'intervention en un scan."
        },
        "facility": {
          "title": "Facility management",
          "desc": "Gestion technique de bâtiments et espaces partagés avec traçabilité complète et rapports automatiques."
        }
      },
      "footer": {
        "contact": "Contact",
        "privacy": "Politique de confidentialité",
        "description": "Plateforme de maintenance digitale par QR code.",
        "rights": "Tous droits reserves."
      },
      "qr_app_not_found": "Application introuvable.",
      "qr_limit_reached": "Cette application a atteint la limite de 5 QR codes du plan gratuit.",
      "qr_limit_exceeded": "Cette génération dépasse le nombre de QR codes restants pour le plan gratuit.",
      "qr_limit_dialog_eyebrow": "Plan gratuit",
      "qr_limit_dialog_title": "Limite du plan gratuit atteinte",
      "qr_limit_dialog_usage_label": "Utilisation des QR codes",
      "qr_limit_dialog_usage": "{{used}}/{{limit}} QR codes utilisés. Restant : {{remaining}}.",
      "qr_limit_dialog_benefit_qr": "QR codes illimités pour vos équipements",
      "qr_limit_dialog_benefit_team": "Gestion d’équipe et accès web/mobile",
      "qr_limit_dialog_benefit_reports": "Rapports d’intervention centralisés",
      "qr_limit_dialog_subscribe": "S’abonner",
      "subscription_app_missing": "Application manquante pour activer l’abonnement.",
      "subscription_plan_invalid": "Offre d’abonnement invalide.",
      "subscription_mock_success": "Abonnement activé.",
      "subscription_mock_error": "Erreur lors de l’activation de l’abonnement.",
      "download_app": "Télécharger l'application",
      "assist_by_scan": "ASSIST BY SCAN",
      "login_title": "Connexion",
      "email": "Email",
      "password": "Mot de passe",
      "login": "Se connecter",
      "forgot_password": "Mot de passe oublié ?",
      "no_account": "Pas de compte ?",
      "signup": "Créer un compte",
      "nav_login": "Connexion",
      "nav_signup": "Inscription",
      "msg_enter_email_password": "Veuillez saisir votre email et votre mot de passe.",
      "msg_invalid_email": "Veuillez saisir une adresse email valide.",
      "msg_unknown_error": "Erreur inconnue.",
      "msg_server_or_network": "Erreur serveur ou réseau.",
      "msg_incorrect_email_or_password": "Email ou mot de passe incorrect.",
      "msg_login_success": "Connexion réussie !",
      "admin_dashboard": "Tableau de bord admin",
      "admin_loading_users": "Chargement des utilisateurs...",
      "admin_error_fetch_users": "Erreur lors du chargement des utilisateurs.",
      "admin_error_delete_user": "Erreur lors de la suppression de l'utilisateur.",
      "admin_confirm_delete": "Êtes-vous sûr de vouloir supprimer cet utilisateur ?",
      "admin_th_id": "ID",
      "admin_th_application": "Application",
      "admin_th_type": "Type",
      "admin_th_email": "Email",
      "admin_th_city": "Ville",
      "admin_th_country": "Pays",
      "admin_th_status": "Statut",
      "admin_th_role": "Rôle",
      "admin_th_qr_count": "Nombre de QR codes",
      "admin_th_actions": "Actions",
      "status_active": "Actif",
      "status_not_active": "Inactif",
      "logout": "Déconnexion",
      "qr_generator": "Générateur QR",
      "questions": "Questions",
      "report": "Rapport",
      "static_page": "Page statique",
      "help_center": "Centre d'aide",
      "register_admin": "Enregistrer admininistrateur",
      "register_user": "Enregistrer utilisateur",
      "contact_us": "Contact",
      "contact_title": "Contact",
      "contact_direct_title": "Ou contactez-nous directement",
      "contact_phone_label": "Téléphone",
      "contact_subtitle": "Besoin d'aide ? Envoyez-nous un message et nous répondrons dès que possible.",
      "contact_name": "Votre nom",
      "contact_email": "Votre email",
      "contact_subject": "Sujet",
      "contact_message_placeholder": "Écrivez votre message...",
      "contact_send": "Envoyer le message",
      "contact_sending": "Envoi en cours...",
      "contact_reset": "Réinitialiser",
      "contact_all_required": "Tous les champs sont obligatoires.",
      "contact_message_short": "Le message est trop court (minimum 10 caractères).",
      "contact_success": "Message envoyé avec succès.",
      "contact_failed": "Échec de l'envoi du message.",
      "contact_error_general": "Erreur lors de l'envoi du message. Veuillez réessayer.",
      "contact_email_label": "Email",
      "contact_name_required": "Le nom est requis.",
      "contact_subject_required": "Le sujet est requis.",
      "contact_message_required": "Le message est requis.",
      "reset_title": "Créer un nouveau mot de passe",
      "reset_verifying": "Vérification du lien…",
      "reset_new_password": "Nouveau mot de passe",
      "reset_confirm_password": "Confirmer le mot de passe",
      "reset_saving": "Enregistrement…",
      "save": "Enregistrer",
      "show": "Afficher",
      "hide": "Masquer",
      "reset_remember": "Vous vous en souvenez ?",
      "reset_fill_both_fields": "Veuillez remplir les deux champs.",
      "reset_pwd_rules": "Mot de passe trop faible : 8+ caractères, majuscule, chiffre, caractère spécial requis.",
      "reset_missing_link": "Lien de réinitialisation manquant.",
      "reset_missing_link_long": "Lien de réinitialisation manquant. Merci d’utiliser le lien reçu par e-mail.",
      "reset_success_redirect": "Mot de passe mis à jour. Redirection…",
      "reset_err_invalid_or_expired": "Lien invalide ou expiré.",
      "reset_err_unexpected": "Erreur inattendue.",
      "reset_err_server": "Erreur serveur.",
      "reset_err_weak_password": "Mot de passe trop faible.",
      "reset_err_password_mismatch": "Les mots de passe ne correspondent pas.",
      "reset_err_expired": "Le lien a expiré. Demandez un nouveau lien.",
      "reset_err_missing_fields": "Veuillez remplir tous les champs.",
      "reset_err_invalid_or_used": "Lien invalide ou déjà utilisé.",
      "reset_processed": "Si le compte existe, le mot de passe a été traité.",
      "download_title": "Installer Assist by Scan",
      "download_mobile_text": "Appuyez sur le bouton ci-dessous pour télécharger l'application :",
      "download_desktop_text": "Scannez ce QR code avec votre appareil Android pour télécharger l'application :",
      "download_apk": "Télécharger l'application (APK)",
      "download_feature_android": "Android terrain",
      "download_feature_scan": "Scan QR instantané",
      "download_feature_secure": "Accès sécurisé",
      "download_holo_scan_fast": "Scan rapide",
      "download_holo_apk_secure": "APK sécurisé",
      "download_tap": "Tap",
      "download_scan": "Scan",
      "editable_placeholder": "Modifiez le texte de {{title}} ici...",
      "editable_load_failed": "Impossible de charger le texte de « {{title}} ».",
      "editable_text_empty": "Le texte ne peut pas être vide.",
      "editable_update_success": "Le texte de « {{title}} » a été mis à jour avec succès.",
      "editable_update_failed": "Échec de la mise à jour.",
      "edit": "Modifier",
      "saving": "Enregistrement…",
      "cancel": "Annuler",
      "forgot_title": "Mot de passe oublié",
      "forgot_email_required": "Email requis.",
      "forgot_email_sent": "Si le compte existe, un email de réinitialisation a été envoyé.",
      "forgot_send_link": "Envoyer le lien de réinitialisation",
      "forgot_sending": "Envoi…",
      "forgot_resend_in": "Renvoyer dans {{seconds}}s",
      "forgot_remember": "Vous vous en souvenez ?",
      "help_title": "Gestion des tâches d'aide",
      "loading": "Chargement...",
      "help_load_failed": "Échec du chargement des tâches.",
      "help_add_new": "Ajouter une nouvelle tâche",
      "help_new_title_ph": "Titre de la nouvelle tâche",
      "help_new_content_ph": "Contenu de la nouvelle tâche",
      "help_add_btn": "Ajouter",
      "help_add_required": "Le titre et le contenu sont obligatoires.",
      "help_app_missing": "Application manquante (localStorage userApplication).",
      "help_add_success": "Nouvelle tâche ajoutée.",
      "help_add_failed": "Échec de l'ajout :",
      "help_item_empty": "Le titre et le contenu ne peuvent pas être vides.",
      "help_item_saved": "Tâche enregistrée avec succès.",
      "help_item_save_failed": "Échec de l'enregistrement :",
      "help_confirm_delete_task": "Êtes-vous sûr de vouloir supprimer cette tâche ?",
      "help_deleted": "Supprimé.",
      "help_delete_failed": "Échec de la suppression.",
      "help_toggle_title": "Cliquez pour afficher/masquer la description",
      "help_upload_video": "Téléverser une vidéo",
      "help_change_video": "Changer la vidéo",
      "help_video_badge": "vidéo",
      "help_delete_video": "Supprimer la vidéo",
      "help_default_1_title": "Comment scanner un QR code ?",
      "help_default_1_content": "Ouvrez l'application mobile, appuyez sur le bouton Scanner et pointez la caméra vers le QR code collé sur la machine.",
      "help_default_2_title": "Comment générer un rapport ?",
      "help_default_2_content": "Après avoir complété une intervention, cliquez sur Générer le rapport pour créer un PDF automatique avec toutes les données.",
      "help_default_3_title": "Comment ajouter un utilisateur ?",
      "help_default_3_content": "Allez dans Enregistrer utilisateur, remplissez le formulaire avec l'email, le nom d'utilisateur et le rôle souhaité.",
      "help_choose_video": "Veuillez choisir un fichier vidéo.",
      "help_video_uploaded": "Vidéo téléversée.",
      "help_upload_failed": "Échec du téléversement :",
      "help_tasks_required_params": "Paramètre application manquant.",
      "help_tasks_not_found": "Aucune tâche d'aide trouvée.",
      "help_tasks_success": "Tâches chargées.",
      "help_tasks_server_error": "Erreur serveur lors du chargement des tâches.",
      "help_task_not_found": "Tâche introuvable.",
      "help_video_missing_field": "Champ 'video' manquant (multipart/form-data).",
      "help_no_file_selected": "Aucun fichier sélectionné.",
      "help_invalid_video_format": "Format vidéo invalide. Formats autorisés : mp4, webm, ogg, mov, m4v.",
      "qr_title": "Générateur de QR Codes",
      "qr_views_aria": "Vues QR",
      "qr_tab_generate": "Générer des QR codes",
      "qr_tab_history": "Voir l'historique",
      "qr_count_aria": "Nombre de QR codes à générer",
      "qr_size_aria": "Taille du QR code en cm",
      "qr_size_option": "{{size}} cm",
      "generate": "Générer",
      "qr_generating": "Génération…",
      "qr_generation_progress": "Génération en cours… ⏳",
      "qr_download_a4_pdf": "Télécharger le PDF A4",
      "qr_download_pdf": "Télécharger le PDF",
      "prev": "← Précédent",
      "next": "Suivant →",
      "page": "Page",
      "of": "sur",
      "qr_loading_history": "Chargement de l’historique… ⏳",
      "qr_no_history": "Aucun QR code dans l’historique.",
      "qr_status_active": "Actif",
      "qr_status_not_active": "Inactif",
      "print": "Imprimer",
      "qr_print_title": "Imprimer QR",
      "qr_alt": "QR code pour {{code}}",
      "qr_confirm_delete": "Voulez-vous vraiment supprimer ce QR code ?",
      "qr_err_invalid_number": "Veuillez saisir un nombre valide (≥ 1).",
      "qr_err_generate": "Erreur lors de la génération des QR codes. Veuillez réessayer.",
      "qr_err_delete": "Erreur lors de la suppression du QR code.",
      "qr_err_history": "Erreur lors du chargement de l’historique. Veuillez réessayer.",
      "qr_err_pdf_single": "Échec du téléchargement du PDF. Vérifiez CORS / accès image.",
      "qr_err_pdf_export": "Échec de l’export PDF. Vérifiez CORS / accès image.",
      "qr_app_missing": "Application manquante.",
      "qr_generate_success": "QR codes générés.",
      "qr_history_success": "Historique chargé.",
      "qr_not_found": "QR code introuvable.",
      "qr_delete_active_forbidden": "Impossible de supprimer un QR code actif.",
      "qr_delete_success": "QR code supprimé avec succès.",
      "qf_type_required": "Le type de machine est obligatoire.",
      "qf_type_added": "Type de machine ajouté.",
      "qf_type_cannot_empty": "Le type ne peut pas être vide.",
      "qf_type_updated": "Type de machine mis à jour.",
      "qf_confirm_delete_type": "Supprimer ce type de machine (et TOUTES ses questions) ?",
      "qf_type_deleted": "Type de machine supprimé.",
      "qf_app_missing": "Application manquante (localStorage userApplication).",
      "qf_select_machine_type_placeholder": "— Sélectionner un type de machine —",
      "qf_type_open": "Ouverte",
      "qf_type_qcm": "QCM",
      "qf_type_yesno": "Oui / Non",
      "qf_qcm_options_ph": "Options séparées par des virgules : ex. \"Faible, Moyen, Élevé\"",
      "qf_question_ph": "Écrivez votre question ici…",
      "qf_it_report_repair": "Rapport de réparation",
      "qf_it_ask_repair": "Demande d’intervention",
      "qf_filter_ask_repair": "Demande réparation",
      "qf_question_added": "Question ajoutée avec succès.",
      "qf_question_deleted": "Question supprimée.",
      "qf_question_deleted_with_id": "Question {{id}} supprimée.",
      "qf_question_updated": "Question mise à jour.",
      "qf_confirm_delete_question": "Supprimer cette question ?",
      "qf_options": "Options",
      "qf_type_badge": "Type",
      "qf_machine_types_title": "Types de machines",
      "qf_add_type_ph": "Ajouter un type de machine…",
      "qf_add_type_btn": "Ajouter",
      "qf_add_question_title": "Ajouter une question",
      "qf_questions_list_title": "Liste des questions",
      "qf_filter_all": "Filtre : Tous",
      "qf_col_question": "Question",
      "qf_col_type": "Type",
      "qf_col_machine": "Machine",
      "qf_col_intervention": "Intervention",
      "qf_options_placeholder": "Option 1, Option 2, Option 3",
      "qf_filters_aria": "Filtres de questions",
      "qf_delete_selected": "Supprimer la sélection",
      "qf_delete_selected_success": "{{count}} question(s) supprimée(s).",
      "qf_select_intervention": "Veuillez choisir le type d’intervention.",
      "qf_select_machine_type": "Veuillez sélectionner un type de machine.",
      "qf_text_required": "Veuillez saisir une question.",
      "qf_text_cannot_empty": "Le texte ne peut pas être vide.",
      "qf_invalid_question_type": "Type de question invalide.",
      "qf_qcm_need_options": "Pour un QCM, vous devez fournir des options.",
      "qf_select_at_least_one": "Sélectionnez au moins une question.",
      "qf_err_load_types": "Impossible de charger les types de machines.",
      "qf_err_load_questions": "Impossible de charger les questions.",
      "qf_type_add_failed": "Échec de l’ajout du type de machine.",
      "qf_type_update_failed": "Échec de la mise à jour du type de machine.",
      "qf_type_delete_failed": "Échec de la suppression du type de machine.",
      "qf_type_delete_conflict": "Impossible de supprimer : ce type est déjà utilisé.",
      "qf_question_add_failed": "Échec de l’ajout de la question.",
      "qf_question_update_failed": "Échec de la mise à jour de la question.",
      "qf_question_delete_failed": "Échec de la suppression de la question.",
      "qf_delete_selected_failed": "Échec de la suppression de la sélection.",
      "qf_select_question_aria": "Sélectionner la question {{id}}",
      "pwd_success_title": "Mot de passe modifié",
      "pwd_success_main": "Votre mot de passe a été modifié avec succès.",
      "pwd_success_hint": "Vous pouvez fermer cette page et rouvrir l’application pour vous connecter.",
      "close": "Fermer",
      "choose_file": "Choisir un fichier",
      "no_file_selected": "Aucun fichier sélectionné",
      "uploading": "Téléversement…",
      "upload": "Téléverser",
      "replace": "Remplacer",
      "help_video_choose_hint": "Choisissez une vidéo depuis votre PC.",
      "help_video_replace_hint": "Choisissez une nouvelle vidéo pour remplacer l'ancienne.",
      "signup_title": "Créer un compte",
      "city": "Ville",
      "country": "Pays",
      "signup_confirm_password": "Confirmer le mot de passe",
      "signup_loading": "Inscription…",
      "signup_have_account": "Vous avez déjà un compte ?",
      "show_password": "Afficher le mot de passe",
      "hide_password": "Masquer le mot de passe",
      "signup_fix_errors": "Veuillez corriger les erreurs en rouge.",
      "signup_success_generic": "Si l’email est valide, un lien de vérification a été envoyé.",
      "signup_err_email_required": "Email requis.",
      "signup_err_city_required": "Ville requise.",
      "signup_err_application_required": "Application requise.",
      "signup_err_country_required": "Pays requis.",
      "signup_err_password_required": "Mot de passe requis.",
      "signup_err_password_weak": "Min 8 caractères, inclure une majuscule, un chiffre et un caractère spécial.",
      "signup_err_confirm_required": "Veuillez confirmer votre mot de passe.",
      "signup_err_password_mismatch": "Les mots de passe ne correspondent pas.",
      "signup_err_invalid_value": "Valeur invalide.",
      "signup_err_validation": "Erreurs de validation.",
      "signup_err_generic": "Erreur d’inscription. Veuillez réessayer plus tard.",
      "signup_err_email_exists": "Email déjà enregistré.",
      "signup_err_application_exists": "Application déjà enregistrée.",
      "static_about": "À propos",
      "static_terms": "Conditions d’utilisation",
      "static_privacy": "Politique de confidentialité",
      "add_user": "Ajouter un utilisateur",
      "username": "Nom d'utilisateur",
      "select_role": "Sélectionner un rôle",
      "role": "Rôle",
      "role_technician": "Technicien",
      "role_client": "Client",
      "specialty": "Spécialité",
      "sector": "Secteur",
      "register": "Enregistrer",
      "registering": "Enregistrement...",
      "users_list": "Liste des utilisateurs",
      "status": "Statut",
      "actions": "Actions",
      "active": "Actif",
      "not_active": "Non actif",
      "delete": "Supprimer",
      "no_users": "Aucun utilisateur trouvé.",
      "all_fields_required": "Tous les champs sont requis.",
      "tech_fields_required": "Spécialité et secteur sont requis pour les techniciens.",
      "user_registered": "Utilisateur enregistré avec succès.",
      "registration_error": "Erreur d'enregistrement.",
      "registration_failed": "Échec de l'enregistrement.",
      "confirm_delete_user": "Voulez-vous vraiment supprimer cet utilisateur ?",
      "user_deleted": "Utilisateur supprimé avec succès.",
      "delete_failed": "Échec de suppression.",
      "delete_error": "Erreur lors de la suppression.",
      "user_load_failed": "Impossible de charger les utilisateurs.",
      "user_fetch_error": "Erreur lors du chargement des utilisateurs.",
      "urw_title": "Ajouter un nouvel administrateur",
      "urw_ph_email": "Email *",
      "urw_ph_application": "Application *",
      "urw_role_legend": "Choisir un rôle :",
      "urw_role_user": "Utilisateur",
      "urw_role_admin": "Admin",
      "urw_type_legend": "Choisir un type :",
      "urw_type_scan": "Scan",
      "urw_type_direct": "Direct",
      "urw_type_both": "Les deux",
      "urw_register": "Enregistrer",
      "urw_registering": "Enregistrement…",
      "urw_fill_required": "Veuillez remplir tous les champs obligatoires.",
      "urw_success": "Utilisateur enregistré avec succès !",
      "urw_failed": "Échec de l’enregistrement.",
      "urw_db_error": "Email déjà enregistré ou erreur base de données.",
      "verify_verifying_title": "Vérification en cours…",
      "verify_verifying_text": "Veuillez patienter un instant.",
      "verify_success_title": "Succès",
      "verify_success_register": "Votre email a été vérifié avec succès.",
      "verify_success_change_email": "Votre adresse email a été modifiée avec succès.",
      "verify_success_delete": "Votre compte a été supprimé.",
      "verify_fail_title": "Échec de la vérification",
      "verify_missing_token": "Jeton de vérification manquant.",
      "verify_invalid": "Lien invalide ou expiré.",
      "verify_failed": "La vérification a échoué.",
      "verify_go_login": "Aller à la connexion",
      "verify_back_login": "Retour à la connexion",
      "VERIFY_TOKEN_MISSING": "Jeton de vérification manquant.",
      "VERIFY_TOKEN_INVALID_OR_USED": "Lien invalide ou déjà utilisé.",
      "VERIFY_TOKEN_EXPIRED": "Le lien a expiré.",
      "VERIFY_INVALID_PAYLOAD": "Données de vérification invalides.",
      "verify_invalid_flow": "Flux de vérification invalide.",
      "verify_db_error": "Erreur serveur (base de données).",
      "verify_new_email_already_in_use": "Cette nouvelle adresse email est déjà utilisée.",
      "VERIFY_REGISTER_SUCCESS": "Votre email a été vérifié avec succès.",
      "verify_change_email_success": "Votre adresse email a été modifiée avec succès.",
      "verify_delete_account_success": "Votre compte a été supprimé avec succès.",
      "msg_email_required": "Email requis.",
      "msg_no_data_received": "Aucune donnée reçue.",
      "msg_password_required": "Le mot de passe est requis.",
      "msg_database_error": "Erreur base de données.",
      "msg_processing_error": "Erreur de traitement.",
      "admin_user_not_found": "Utilisateur introuvable.",
      "admin_user_deleted_success": "Utilisateur supprimé avec succès.",
      "MACHINE_TYPES_REQUIRED_APP": "Application requise.",
      "MACHINE_TYPES_SUCCESS": "Types chargés.",
      "MACHINE_TYPES_TYPE_REQUIRED": "Le champ « type » est requis.",
      "MACHINE_TYPES_ADDED": "Type de machine ajouté.",
      "MACHINE_TYPES_ALREADY_EXISTS": "Ce type existe déjà pour cette application.",
      "MACHINE_TYPES_NOT_FOUND": "Type de machine introuvable.",
      "MACHINE_TYPES_UPDATED": "Type de machine mis à jour.",
      "MACHINE_TYPES_DELETED": "Type de machine supprimé.",
      "MACHINE_TYPES_FORBIDDEN": "Accès refusé.",
      "MACHINE_TYPES_SERVER_ERROR": "Erreur serveur.",
      "QUESTIONS_REQUIRED_APP": "Application requise.",
      "QUESTIONS_SUCCESS": "Questions chargées.",
      "QUESTIONS_TEXT_REQUIRED": "Le texte est requis.",
      "QUESTIONS_MACHINE_TYPE_REQUIRED": "Le type de machine est requis.",
      "QUESTIONS_INVALID_INTERVENTION": "Type d’intervention invalide.",
      "QUESTIONS_INVALID_TYPE": "Type de question invalide.",
      "QUESTIONS_QCM_OPTIONS_REQUIRED": "Pour un QCM, les options sont obligatoires.",
      "QUESTIONS_ADDED": "Question ajoutée.",
      "QUESTIONS_NOT_FOUND": "Question introuvable.",
      "QUESTIONS_DELETED": "Question supprimée (ID : {{id}}).",
      "QUESTIONS_UPDATED": "Question mise à jour.",
      "QUESTIONS_SERVER_ERROR": "Erreur serveur.",
      "repport_title": "Gestion des rapports",
      "repport_tabs_aria": "Onglets rapports",
      "repport_tab_manage": "Gestion",
      "repport_tab_history": "Historique",
      "repport_new_title_placeholder": "Nouveau titre de rapport...",
      "repport_new_subtitle_placeholder": "Nouveau sous-titre...",
      "repport_search_placeholder": "Rechercher par username, QR ID, numéro de série ou technicien...",
      "repport_col_date": "Date",
      "repport_col_user": "Utilisateur",
      "repport_col_technician": "Technicien",
      "repport_col_qr": "QR ID",
      "repport_detail_title": "Détails du rapport",
      "repport_serial": "N° série",
      "repport_answers": "Réponses",
      "repport_field_question": "Question",
      "repport_field_type": "Type",
      "repport_add_title": "Ajouter un nouveau titre",
      "repport_title_ph": "Titre",
      "repport_add_title_btn": "Ajouter le titre",
      "repport_loading_titles": "Chargement des titres…",
      "repport_no_titles": "Aucun titre pour le moment.",
      "repport_toggle_title": "Cliquer pour afficher/masquer les sous-titres",
      "repport_delete_title": "Supprimer ce titre",
      "repport_loading_subtitles": "Chargement des sous-titres…",
      "repport_no_subtitles": "Aucun sous-titre pour ce titre.",
      "repport_toggle_subtitle": "Cliquer pour afficher/masquer les questions",
      "repport_add_subtitle": "Ajouter un sous-titre",
      "repport_add_subtitle_for_title": "Ajouter un sous-titre pour ce titre",
      "repport_subtitle_ph": "Nouveau sous-titre",
      "repport_add_subtitle_btn": "Ajouter le sous-titre",
      "repport_loading_questions": "Chargement des questions…",
      "repport_no_questions": "Aucune question pour ce sous-titre.",
      "repport_add_question": "Ajouter une question",
      "repport_add_question_btn": "Ajouter la question",
      "repport_question_text_ph": "Texte de la question",
      "repport_qcm_options": "Options (séparées par des virgules)",
      "repport_qcm_options_ph": "Ex : \"Faible, Moyen, Élevé\"",
      "repport_loading_history": "Chargement de l’historique…",
      "repport_no_submissions": "Aucune soumission trouvée.",
      "repport_filter_label": "Filtrer par client (username)",
      "repport_filter_ph": "Tapez un username… (ex : ehsan)",
      "repport_showing": "Affichage de",
      "repport_results": "résultat(s)",
      "repport_row_qr": "QR :",
      "repport_row_serial": "Numéro de série :",
      "repport_details": "Détails du rapport",
      "repport_building": "Construction du rapport…",
      "repport_submitted": "Soumis",
      "repport_download_pdf": "Télécharger PDF",
      "repport_pdf_generating": "Génération du PDF...",
      "repport_answer": "Réponse",
      "repport_unanswered": "Sans réponse",
      "repport_no_questions_in_submission": "Aucune question dans cette soumission.",
      "repport_pdf_title": "Rapport",
      "repport_pdf_date": "Date",
      "repport_pdf_qr_id": "QR ID",
      "repport_pdf_serial": "Numéro de série",
      "repport_pdf_username": "Utilisateur",
      "repport_pdf_technician": "Technicien",
      "repport_th_question": "Question",
      "repport_th_answer": "Réponse",
      "repport_type": "Type",
      "repport_type_open": "Ouverte",
      "repport_type_qcm": "QCM",
      "repport_type_yesno": "Oui / Non",
      "repport_popup_blocked": "Popup bloquée. Autorisez les popups pour télécharger le PDF.",
      "repport_confirm_delete_title": "Supprimer le titre \"{{title}}\" (tous ses sous-titres & questions) ?",
      "repport_confirm_delete_subtitle": "Supprimer le sous-titre \"{{subtitle}}\" et toutes ses questions ?",
      "repport_confirm_delete_question": "Supprimer cette question ?",
      "repport_err_app_missing": "Application non définie (localStorage).",
      "repport_err_title_required": "Le titre est obligatoire.",
      "repport_err_title_exists": "Ce titre existe déjà pour cette application.",
      "repport_err_title_add_failed": "Échec lors de l’ajout du titre.",
      "repport_err_title_delete_failed": "Échec de suppression du titre.",
      "repport_err_select_title_first": "Sélectionnez un titre d’abord.",
      "repport_err_subtitle_required": "Le sous-titre est obligatoire.",
      "repport_err_subtitle_exists": "Ce sous-titre existe déjà.",
      "repport_err_subtitle_add_failed": "Échec lors de l’ajout du sous-titre.",
      "repport_err_subtitle_delete_failed": "Échec de suppression du sous-titre.",
      "repport_err_select_title_subtitle_first": "Sélectionnez un titre et un sous-titre d’abord.",
      "repport_err_question_text_required": "Le texte de la question est obligatoire.",
      "repport_err_invalid_question_type": "Type de question invalide.",
      "repport_err_qcm_options_required": "Pour un QCM, ajoutez au moins une option.",
      "repport_question_added": "Question ajoutée.",
      "repport_err_question_add_failed": "Échec lors de l’ajout de la question.",
      "repport_err_question_delete_failed": "Échec de suppression de la question.",
      "repport_title_added": "Titre ajouté.",
      "repport_subtitle_added": "Sous-titre ajouté.",
      "repport_err_titles_load": "Impossible de charger les titres.",
      "repport_err_subtitles_load": "Impossible de charger les sous-titres.",
      "repport_err_questions_load": "Impossible de charger les questions.",
      "repport_err_history_load": "Impossible de charger l’historique.",
      "repport_err_build_report": "Impossible de construire le rapport (meta/questions).",
      "repport_err_pdf_download": "Impossible de télécharger le PDF pour le moment.",
      "repport_history_app_required": "L'application est obligatoire.",
      "repport_history_success": "Historique chargé avec succès.",
      "repport_history_db_error": "Erreur base de données lors du chargement de l'historique.",
      "repport_meta_required_ids": "Les identifiants des questions sont requis.",
      "repport_meta_no_valid_ids": "Aucun identifiant valide fourni.",
      "repport_meta_success": "Métadonnées chargées avec succès.",
      "repport_meta_db_error": "Erreur base de données lors du chargement des métadonnées.",
      "ur_app_required": "Le paramètre application est obligatoire.",
      "ur_users_loaded": "Utilisateurs chargés.",
      "ur_server_error": "Erreur serveur. Veuillez réessayer plus tard.",
      "ur_missing_fields": "Champs obligatoires manquants.",
      "ur_tech_fields_required": "Spécialité et secteur sont requis pour les techniciens.",
      "ur_email_exists": "Cet email est déjà enregistré.",
      "ur_username_exists": "Ce nom d'utilisateur est déjà utilisé pour cette application.",
      "ur_register_success": "Utilisateur enregistré avec succès.",
      "ur_id_required": "Champ obligatoire manquant : id.",
      "ur_user_not_found": "Utilisateur introuvable.",
      "ur_delete_success": "Utilisateur supprimé avec succès.",
      "na": "N/A",
      "unknown": "Inconnu",
      "client": "Client",
      "technician": "Technicien",
      "type": "Type",
      "required": "Obligatoire",
      "view": "Voir",
      "rp_qr": "QR code",
      "no_data": "Aucune donnée",
      "signature": "Signature",
      "add": "Ajouter",
      "admin_manage_apps_users": "Gerer applications et utilisateurs",
      "admin_users_total": "Total utilisateurs",
      "application": "Application",
      "auth_login_story_text": "Connectez-vous pour acceder a vos QR codes, rapports et interventions.",
      "auth_login_story_title": "Bon retour",
      "auth_proof_access": "Acces controle",
      "auth_proof_qr": "Machines connectees",
      "auth_proof_reports": "Rapports automatiques",
      "auth_signup_feature_1": "Gestion des QR codes",
      "auth_signup_feature_2": "Rapports intelligents",
      "auth_signup_feature_3": "Planification des interventions",
      "auth_signup_story_text": "Creez votre espace et commencez a digitaliser votre maintenance.",
      "auth_signup_story_title": "Demarrez avec AssistByScan",
      "darkMode": {
        "toggle": "Basculer le mode sombre"
      },
      "date": "Date",
      "details": "Details",
      "filter": "Filtre",
      "forgot_intro": "Saisissez votre email pour recevoir un lien de reinitialisation.",
      "forgot_resend_window": "Vous pourrez renvoyer un email dans {{seconds}}s.",
      "forgot_secure_link": "Lien securise envoye par email.",
      "forgot_story_title": "Reinitialisez votre mot de passe",
      "help_replace_video": "Remplacer la video",
      "language": "Langue",
      "qr_generated_title": "QR codes generes",
      "reset_success": "Votre mot de passe a ete reinitialise avec succes.",
      "rp_popup_blocked": "Fenetre bloquee. Autorisez les popups.",
      "static_title": "Pages statiques",
      "theme_dark": "Mode sombre",
      "theme_light": "Mode clair",
      "urw_add_other": "Ajouter un autre utilisateur",
      "urw_subtitle": "Enregistrez des utilisateurs pour votre application.",
      "urw_success_title": "Utilisateur enregistre avec succes"
    }
  },
  "en": {
    "translation": {
      "nav": {
        "features": "Features",
        "howItWorks": "How it works",
        "pricing": "Pricing",
        "contact": "Contact",
        "login": "Login",
        "home": "Home",
        "register": "Register"
      },
      "hero": {
        "badge": "SaaS Platform · Digital maintenance by QR Code",
        "title": "Digitalize your technical interventions with a simple scan",
        "subtitle": "Each machine linked to a QR code, each intervention tracked, each report automatically generated. A web + mobile platform to centralize and automate all your maintenance.",
        "login": "Login",
        "signup": "Sign up",
        "discover": "Discover"
      },
      "pricing": {
        "label": "Pricing",
        "title": "Try for free, pay only if you're convinced",
        "subtitle": "Start with the free trial, then activate Pro with a demo card payment when you're ready.",
        "noCreditCard": "Demo card payment",
        "noCommitment": "No commitment",
        "cancelAnytime": "Cancel anytime",
        "payment": {
          "demoBadge": "Demo card payment",
          "title": "Activate your Pro subscription",
          "subtitle": "Complete activation with a test card. No real charge is made in this mode.",
          "cardName": "Name on card",
          "cardNamePlaceholder": "Jane Smith",
          "cardNumber": "Card number",
          "expiry": "Expiry",
          "cvc": "CVC",
          "demoNotice": "Inactive payment mode: this validation simulates payment and activates the subscription for your application.",
          "mockOnly": "Simulated transaction",
          "noCharge": "No bank charge",
          "activateNow": "Immediate activation",
          "missingApp": "Sign in with your application before activating a subscription.",
          "invalid": "Please complete the test card information.",
          "processing": "Activating…",
          "payButton": "Activate subscription",
          "success": "Subscription activated. You can now generate more QR codes.",
          "backToQr": "Back to QR generator"
        },
        "plans": [
          {
            "name": "Free Trial",
            "price": "€0",
            "period": "for 14 days",
            "description": "Test all features with no commitment or credit card.",
            "badge": "14 days free",
            "cta": "Start free trial",
            "features": [
              "Full platform access",
              "5 QR codes included",
              "Up to 5 users",
              "Dynamic forms",
              "PDF reports",
              "Email support"
            ]
          },
          {
            "name": "Pro",
            "price": "€149",
            "period": "/month",
            "description": "After your trial, switch to Pro to continue without interruption.",
            "badge": "",
            "cta": "Choose Pro plan",
            "features": [
              "Everything in free trial",
              "Unlimited users",
              "Advanced scheduling",
              "Real-time notifications",
              "API access",
              "Priority support",
              "Advanced PDF export"
            ]
          },
          {
            "name": "Enterprise",
            "price": "Custom",
            "period": "",
            "description": "For large organizations with specific and multi-site needs.",
            "badge": "",
            "cta": "Contact us",
            "features": [
              "Everything in Pro",
              "SSO & SAML",
              "Guaranteed SLA",
              "Dedicated account manager",
              "On-premise deployment",
              "On-site training"
            ]
          }
        ]
      },
      "features": {
        "badge": "Features",
        "title": "Everything you need to digitalize your maintenance",
        "subtitle": "A complete platform that covers every step, from QR scan to final report export.",
        "qrcodes": {
          "title": "Smart QR Codes",
          "desc": "Each machine is linked to a unique QR code. A simple scan opens the form adapted to the equipment and intervention context."
        },
        "forms": {
          "title": "Dynamic Forms",
          "desc": "Create hierarchical questionnaires (titles, subtitles, questions) with free answers, multiple choices or yes/no, adapted to each type of intervention."
        },
        "planning": {
          "title": "Smart Planning",
          "desc": "The system automatically suggests available slots based on each technician's schedules, working days and holidays."
        },
        "reports": {
          "title": "Automated Reports",
          "desc": "When an intervention is completed, a structured report is generated with all collected data, viewable and exportable to PDF."
        },
        "notifications": {
          "title": "Real-time Notifications",
          "desc": "Admins, technicians and clients are notified at every step: new request, assignment, completed intervention."
        },
        "roles": {
          "title": "Multi-role Management",
          "desc": "Manage your team with distinct roles (admin, technician, client) and permissions adapted to each profile."
        },
        "mobile": {
          "title": "Mobile Field App",
          "desc": "Technicians and users work from the Android app: scan, diagnosis, input and submission directly in the field."
        },
        "languages": {
          "title": "Multilingual Support",
          "desc": "The interface and messages dynamically adapt to each user's language through a server-side translation system."
        },
        "history": {
          "title": "History & Advanced Filtering",
          "desc": "Find any past intervention using filters by user, machine, technician, date and status."
        }
      },
      "steps": {
        "badge": "How it works",
        "title": "From scan to report in 5 steps",
        "subtitle": "A smooth and automated process, from initial setup to final report generation.",
        "configure": {
          "title": "Configure your machines and forms",
          "desc": "From the web interface, the administrator defines machine types and creates hierarchical forms with questions adapted to each intervention context."
        },
        "qrcode": {
          "title": "Generate and deploy your QR codes",
          "desc": "Each piece of equipment receives a unique QR code. Print them and place them on your machines to make them instantly accessible."
        },
        "scan": {
          "title": "Scan, diagnose and submit",
          "desc": "The user scans the QR code from the mobile app, fills out the adapted form (diagnosis, direct request) and submits their request in seconds."
        },
        "planning": {
          "title": "Automatic planning and assignment",
          "desc": "The system identifies available technicians based on their schedules, skills and constraints, then automatically suggests an intervention slot."
        },
        "report": {
          "title": "Intervention and generated report",
          "desc": "The technician intervenes, completes field data. A structured report is automatically generated, viewable online and exportable to PDF."
        }
      },
      "showcase": {
        "badge": "Product",
        "title": "Web + Mobile, a single platform",
        "subtitle": "The administrator configures and supervises from the web dashboard. Technicians and users scan, diagnose and intervene from the Android mobile application."
      },
      "benefits": {
        "badge": "Benefits",
        "title": "Concrete results from the first month",
        "time": {
          "title": "Save time",
          "desc": "No more searching through history or filling out paper forms. Each intervention is automatically tracked from scan to report.",
          "stat": "processing time"
        },
        "planning": {
          "title": "Better planned interventions",
          "desc": "The smart planning engine takes into account real availability, holidays and each technician's skills.",
          "stat": "more interventions/day"
        },
        "paperless": {
          "title": "Zero paper, zero loss",
          "desc": "Dynamic forms replace notebooks. All data is structured, stored and accessible at any time from any device.",
          "stat": "paper document"
        },
        "data": {
          "title": "Centralized data",
          "desc": "Complete intervention history, exportable PDF reports, advanced filtering by technician, machine, date or status — all in one place.",
          "stat": "traceability"
        }
      },
      "cases": {
        "badge": "Use cases",
        "title": "Adapted to your sector",
        "subtitle": "Whatever your field, AssistByScan adapts to your maintenance processes.",
        "industry": {
          "title": "Industry",
          "desc": "Preventive and corrective maintenance on production lines. Each machine identified by QR code, each intervention tracked."
        },
        "services": {
          "title": "Maintenance services",
          "desc": "Multi-client and multi-site management. Plan, assign and track your technicians' interventions in real time."
        },
        "retail": {
          "title": "Retail & Commerce",
          "desc": "Track equipment in stores (air conditioning, checkouts, lighting) with one-scan intervention requests."
        },
        "facility": {
          "title": "Facility management",
          "desc": "Technical management of buildings and shared spaces with complete traceability and automatic reports."
        }
      },
      "footer": {
        "contact": "Contact",
        "privacy": "Privacy Policy",
        "description": "Digital maintenance platform powered by QR codes.",
        "rights": "All rights reserved."
      },
      "qr_app_not_found": "Application not found.",
      "qr_limit_reached": "This application has reached the limit of 5 QR codes for the free plan.",
      "qr_limit_exceeded": "This request exceeds the remaining number of QR codes allowed for the free plan.",
      "qr_limit_dialog_eyebrow": "Free plan",
      "qr_limit_dialog_title": "Free plan limit reached",
      "qr_limit_dialog_usage_label": "QR code usage",
      "qr_limit_dialog_usage": "{{used}}/{{limit}} QR codes used. Remaining: {{remaining}}.",
      "qr_limit_dialog_benefit_qr": "Unlimited QR codes for your equipment",
      "qr_limit_dialog_benefit_team": "Team management and web/mobile access",
      "qr_limit_dialog_benefit_reports": "Centralized intervention reports",
      "qr_limit_dialog_subscribe": "Subscribe",
      "subscription_app_missing": "Application is missing for subscription activation.",
      "subscription_plan_invalid": "Invalid subscription plan.",
      "subscription_mock_success": "Subscription activated.",
      "subscription_mock_error": "Subscription activation failed.",
      "download_app": "Download App",
      "assist_by_scan": "ASSIST BY SCAN",
      "login_title": "Login",
      "email": "Email",
      "password": "Password",
      "login": "Login",
      "forgot_password": "Forgot password?",
      "no_account": "Don't have an account?",
      "signup": "Sign up",
      "nav_login": "Login",
      "nav_signup": "Sign Up",
      "msg_enter_email_password": "Please enter your email and password.",
      "msg_invalid_email": "Please enter a valid email address.",
      "msg_unknown_error": "Unknown error.",
      "msg_server_or_network": "Server or network error.",
      "msg_incorrect_email_or_password": "Incorrect email or password.",
      "msg_login_success": "Login successful!",
      "admin_dashboard": "Admin Dashboard",
      "admin_loading_users": "Loading users...",
      "admin_error_fetch_users": "Error fetching users.",
      "admin_error_delete_user": "Error deleting user.",
      "admin_confirm_delete": "Are you sure you want to delete this user?",
      "admin_th_id": "ID",
      "admin_th_application": "Application",
      "admin_th_type": "Type",
      "admin_th_email": "Email",
      "admin_th_city": "City",
      "admin_th_country": "Country",
      "admin_th_status": "Status",
      "admin_th_role": "Role",
      "admin_th_qr_count": "QR Code Count",
      "admin_th_actions": "Actions",
      "status_active": "Active",
      "status_not_active": "Not active",
      "logout": "Logout",
      "qr_generator": "QR generator",
      "questions": "Questions",
      "report": "Report",
      "static_page": "Static page",
      "help_center": "Help center",
      "register_admin": "Register admin",
      "register_user": "Register user",
      "contact_us": "Contact Us",
      "contact_title": "Contact",
      "contact_direct_title": "Or contact us directly",
      "contact_phone_label": "Phone",
      "contact_subtitle": "Need help? Send us a message and we will reply as soon as possible.",
      "contact_name": "Your name",
      "contact_email": "Your email",
      "contact_subject": "Subject",
      "contact_message_placeholder": "Write your message...",
      "contact_send": "Send message",
      "contact_sending": "Sending...",
      "contact_reset": "Reset",
      "contact_all_required": "All fields are required.",
      "contact_message_short": "Message is too short (minimum 10 characters).",
      "contact_success": "Message sent successfully.",
      "contact_failed": "Failed to send message.",
      "contact_error_general": "Error sending message. Please try again.",
      "contact_email_label": "Email",
      "contact_name_required": "Name is required.",
      "contact_subject_required": "Subject is required.",
      "contact_message_required": "Message is required.",
      "reset_title": "Create a new password",
      "reset_verifying": "Verifying link…",
      "reset_new_password": "New password",
      "reset_confirm_password": "Confirm password",
      "reset_saving": "Saving…",
      "save": "Save",
      "show": "Show",
      "hide": "Hide",
      "reset_remember": "Remember it?",
      "reset_fill_both_fields": "Please fill in both fields.",
      "reset_pwd_rules": "Password too weak: 8+ chars, uppercase, number, and special character required.",
      "reset_missing_link": "Missing reset link.",
      "reset_missing_link_long": "Reset link is missing. Please use the link received by email.",
      "reset_success_redirect": "Password updated. Redirecting…",
      "reset_err_invalid_or_expired": "Invalid or expired link.",
      "reset_err_unexpected": "Unexpected error.",
      "reset_err_server": "Server error.",
      "reset_err_weak_password": "Password is too weak.",
      "reset_err_password_mismatch": "Passwords do not match.",
      "reset_err_expired": "The link has expired. Request a new link.",
      "reset_err_missing_fields": "Please fill in all fields.",
      "reset_err_invalid_or_used": "Invalid or already used link.",
      "reset_processed": "If the account exists, the password reset was processed.",
      "download_title": "Install Assist by Scan",
      "download_mobile_text": "Tap the button below to download the app:",
      "download_desktop_text": "Scan this QR code with your Android device to download the app:",
      "download_apk": "Download application (APK)",
      "download_feature_android": "Android in the field",
      "download_feature_scan": "Instant QR scan",
      "download_feature_secure": "Secure access",
      "download_holo_scan_fast": "Fast scan",
      "download_holo_apk_secure": "Secure APK",
      "download_tap": "Tap",
      "download_scan": "Scan",
      "editable_placeholder": "Edit your {{title}} text here...",
      "editable_load_failed": "Failed to load \"{{title}}\" text.",
      "editable_text_empty": "Text cannot be empty.",
      "editable_update_success": "\"{{title}}\" text updated successfully.",
      "editable_update_failed": "Failed to update.",
      "edit": "Edit",
      "saving": "Saving...",
      "cancel": "Cancel",
      "forgot_title": "Forgot Password",
      "forgot_email_required": "Email is required.",
      "forgot_email_sent": "If the account exists, a reset email has been sent.",
      "forgot_send_link": "Send reset link",
      "forgot_sending": "Sending...",
      "forgot_resend_in": "Resend in {{seconds}}s",
      "forgot_remember": "Remember your password?",
      "help_title": "Help Tasks Management",
      "loading": "Loading...",
      "help_load_failed": "Failed to load tasks.",
      "help_add_new": "Add New Task",
      "help_new_title_ph": "New task title",
      "help_new_content_ph": "New task content",
      "help_add_btn": "Add",
      "help_add_required": "Title and content are required.",
      "help_app_missing": "Application is missing (localStorage userApplication).",
      "help_add_success": "New task added.",
      "help_add_failed": "Adding failed:",
      "help_item_empty": "Title and content cannot be empty.",
      "help_item_saved": "Task saved successfully.",
      "help_item_save_failed": "Saving failed:",
      "help_confirm_delete_task": "Are you sure you want to delete this task?",
      "help_deleted": "Deleted.",
      "help_delete_failed": "Failed to delete.",
      "help_toggle_title": "Click to toggle description",
      "help_upload_video": "Upload video",
      "help_change_video": "Change video",
      "help_video_badge": "video",
      "help_delete_video": "Delete video",
      "help_default_1_title": "How to scan a QR code?",
      "help_default_1_content": "Open the mobile app, tap the Scan button, and point the camera at the QR code on the machine.",
      "help_default_2_title": "How to generate a report?",
      "help_default_2_content": "After completing an intervention, click Generate report to create an automatic PDF with all data.",
      "help_default_3_title": "How to add a user?",
      "help_default_3_content": "Go to Register user, then fill in the form with email, username, and role.",
      "help_choose_video": "Please choose a video file.",
      "help_video_uploaded": "Video uploaded.",
      "help_upload_failed": "Upload failed:",
      "help_tasks_required_params": "Missing application parameter.",
      "help_tasks_not_found": "No help tasks found.",
      "help_tasks_success": "Tasks loaded.",
      "help_tasks_server_error": "Server error while loading tasks.",
      "help_task_not_found": "Help task not found.",
      "help_video_missing_field": "Missing 'video' field (multipart/form-data).",
      "help_no_file_selected": "No file selected.",
      "help_invalid_video_format": "Invalid video format. Allowed: mp4, webm, ogg, mov, m4v.",
      "qr_title": "QR Code Generator",
      "qr_views_aria": "QR views",
      "qr_tab_generate": "Generate QR Codes",
      "qr_tab_history": "View History",
      "qr_count_aria": "Number of QR codes to generate",
      "qr_size_aria": "QR code size in cm",
      "qr_size_option": "{{size}} cm",
      "generate": "Generate",
      "qr_generating": "Generating…",
      "qr_generation_progress": "Generation in progress… ⏳",
      "qr_download_a4_pdf": "Download A4 PDF",
      "qr_download_pdf": "Download PDF",
      "prev": "← Previous",
      "next": "Next →",
      "page": "Page",
      "of": "of",
      "qr_loading_history": "Loading history… ⏳",
      "qr_no_history": "No QR codes in history.",
      "qr_status_active": "Active",
      "qr_status_not_active": "Not active",
      "print": "Print",
      "qr_print_title": "Print QR",
      "qr_alt": "QR code for {{code}}",
      "qr_confirm_delete": "Do you really want to delete this QR code?",
      "qr_err_invalid_number": "Please enter a valid number (≥ 1).",
      "qr_err_generate": "Error generating QR codes. Please try again.",
      "qr_err_delete": "Error deleting the QR code.",
      "qr_err_history": "Error fetching QR code history. Please try again.",
      "qr_err_pdf_single": "Single PDF download failed. Check CORS / image access.",
      "qr_err_pdf_export": "PDF export failed. Check CORS / image access.",
      "qr_app_missing": "Application is missing.",
      "qr_generate_success": "QR codes generated.",
      "qr_history_success": "History loaded.",
      "qr_not_found": "QR code not found.",
      "qr_delete_active_forbidden": "Cannot delete an active QR code.",
      "qr_delete_success": "QR code deleted successfully.",
      "qf_type_required": "Machine type is required.",
      "qf_type_added": "Machine type added.",
      "qf_type_cannot_empty": "Type cannot be empty.",
      "qf_type_updated": "Machine type updated.",
      "qf_confirm_delete_type": "Delete this machine type (and ALL its questions)?",
      "qf_type_deleted": "Machine type deleted.",
      "qf_app_missing": "application is missing (localStorage userApplication).",
      "qf_select_machine_type_placeholder": "— Select machine type —",
      "qf_type_open": "Open",
      "qf_type_qcm": "QCM",
      "qf_type_yesno": "Yes / No",
      "qf_qcm_options_ph": "Options comma separated: e.g. \"Low, Medium, High\"",
      "qf_question_ph": "Write your question here...",
      "qf_it_report_repair": "Report repair",
      "qf_it_ask_repair": "Repair intervention",
      "qf_filter_ask_repair": "Ask repair",
      "qf_question_added": "Question added successfully.",
      "qf_questions_list_title": "Questions List",
      "qf_filters_aria": "Question filters",
      "qf_delete_selected": "Delete Selected",
      "qf_question_deleted": "Question deleted.",
      "qf_question_deleted_with_id": "Question {{id}} deleted.",
      "qf_question_updated": "Question updated.",
      "qf_confirm_delete_question": "Delete this question?",
      "qf_options": "Options",
      "qf_type_badge": "Type",
      "qf_machine_types_title": "Machine types",
      "qf_add_type_ph": "Add a machine type…",
      "qf_add_type_btn": "Add",
      "qf_add_question_title": "Add a question",
      "qf_filter_all": "Filter: All",
      "qf_col_question": "Question",
      "qf_col_type": "Type",
      "qf_col_machine": "Machine",
      "qf_col_intervention": "Intervention",
      "qf_options_placeholder": "Option 1, Option 2, Option 3",
      "qf_delete_selected_success": "{{count}} question(s) deleted.",
      "qf_select_intervention": "Please choose an intervention type.",
      "qf_select_machine_type": "Please select a machine type.",
      "qf_text_required": "Please enter a question.",
      "qf_text_cannot_empty": "Text cannot be empty.",
      "qf_invalid_question_type": "Invalid question type.",
      "qf_qcm_need_options": "For multiple choice, you must provide options.",
      "qf_select_at_least_one": "Select at least one question.",
      "qf_err_load_types": "Failed to load machine types.",
      "qf_err_load_questions": "Failed to load questions.",
      "qf_type_add_failed": "Failed to add machine type.",
      "qf_type_update_failed": "Failed to update machine type.",
      "qf_type_delete_failed": "Failed to delete machine type.",
      "qf_type_delete_conflict": "Cannot delete: this type is already used.",
      "qf_question_add_failed": "Failed to add question.",
      "qf_question_update_failed": "Failed to update question.",
      "qf_question_delete_failed": "Failed to delete question.",
      "qf_delete_selected_failed": "Failed to delete selection.",
      "qf_select_question_aria": "Select question {{id}}",
      "pwd_success_title": "Password changed",
      "pwd_success_main": "Your password has been successfully changed.",
      "pwd_success_hint": "You can close this page and reopen the application to log in.",
      "close": "Close",
      "choose_file": "Choose file",
      "no_file_selected": "No file selected",
      "uploading": "Uploading...",
      "upload": "Upload",
      "replace": "Replace",
      "help_video_choose_hint": "Choose a video from your PC.",
      "help_video_replace_hint": "Choose a new video to replace the existing one.",
      "signup_title": "Create Account",
      "city": "City",
      "country": "Country",
      "signup_confirm_password": "Confirm Password",
      "signup_loading": "Signing up…",
      "signup_have_account": "Already have an account?",
      "show_password": "Show password",
      "hide_password": "Hide password",
      "signup_fix_errors": "Please fix the highlighted errors.",
      "signup_success_generic": "If the email is valid, a verification link has been sent.",
      "signup_err_email_required": "Email is required.",
      "signup_err_city_required": "City is required.",
      "signup_err_application_required": "Application is required.",
      "signup_err_country_required": "Country is required.",
      "signup_err_password_required": "Password is required.",
      "signup_err_password_weak": "Min 8 chars, include an uppercase, a number, and a special character.",
      "signup_err_confirm_required": "Please confirm your password.",
      "signup_err_password_mismatch": "Passwords do not match.",
      "signup_err_invalid_value": "Invalid value.",
      "signup_err_validation": "Validation errors.",
      "signup_err_generic": "Signup error occurred. Please try again later.",
      "signup_err_email_exists": "Email already registered.",
      "signup_err_application_exists": "App already registered.",
      "static_about": "About Us",
      "static_terms": "Terms of Use",
      "static_privacy": "Privacy Policy",
      "add_user": "Add a User",
      "username": "Username",
      "select_role": "Select a role",
      "role": "Role",
      "role_technician": "Technician",
      "role_client": "Client",
      "specialty": "Specialty",
      "sector": "Sector",
      "register": "Register",
      "registering": "Registering...",
      "users_list": "Users List",
      "status": "Status",
      "actions": "Actions",
      "active": "Active",
      "not_active": "Not Active",
      "delete": "Delete",
      "no_users": "No users found.",
      "all_fields_required": "All fields are required.",
      "tech_fields_required": "Specialty and sector are required for technicians.",
      "user_registered": "User registered successfully.",
      "registration_error": "Registration error.",
      "registration_failed": "Registration failed.",
      "confirm_delete_user": "Are you sure you want to delete this user?",
      "user_deleted": "User deleted successfully.",
      "delete_failed": "Failed to delete user.",
      "delete_error": "Error deleting user.",
      "user_load_failed": "Failed to load users.",
      "user_fetch_error": "Error fetching users.",
      "urw_title": "Add New Administrator",
      "urw_ph_email": "Email *",
      "urw_ph_application": "Application *",
      "urw_role_legend": "Select a role:",
      "urw_role_user": "User",
      "urw_role_admin": "Admin",
      "urw_type_legend": "Select a type:",
      "urw_type_scan": "Scan",
      "urw_type_direct": "Direct",
      "urw_type_both": "Both",
      "urw_register": "Register",
      "urw_registering": "Registering...",
      "urw_fill_required": "Please fill in all required fields.",
      "urw_success": "User registered successfully!",
      "urw_failed": "Registration failed.",
      "urw_db_error": "Email already registered or database error.",
      "verify_verifying_title": "Verifying your request…",
      "verify_verifying_text": "Please wait a moment.",
      "verify_success_title": "Success",
      "verify_success_register": "Your email has been verified.",
      "verify_success_change_email": "Your email address has been changed successfully.",
      "verify_success_delete": "Your account has been deleted.",
      "verify_fail_title": "Verification Failed",
      "verify_missing_token": "Missing verification token.",
      "verify_invalid": "Invalid or expired link.",
      "verify_failed": "Verification failed.",
      "verify_go_login": "Go to Login",
      "verify_back_login": "Back to Login",
      "VERIFY_TOKEN_MISSING": "Missing verification token.",
      "VERIFY_TOKEN_INVALID_OR_USED": "Invalid or already used link.",
      "VERIFY_TOKEN_EXPIRED": "The link has expired.",
      "VERIFY_INVALID_PAYLOAD": "Invalid verification data.",
      "verify_invalid_flow": "Invalid verification flow.",
      "verify_db_error": "Server/database error.",
      "verify_new_email_already_in_use": "This new email address is already in use.",
      "VERIFY_REGISTER_SUCCESS": "Your email has been verified successfully.",
      "verify_change_email_success": "Your email address has been changed successfully.",
      "verify_delete_account_success": "Your account has been deleted successfully.",
      "msg_email_required": "Email is required.",
      "msg_no_data_received": "No data received.",
      "msg_password_required": "Password is required.",
      "msg_database_error": "Database error.",
      "msg_processing_error": "Processing error.",
      "admin_user_not_found": "User not found.",
      "admin_user_deleted_success": "User deleted successfully.",
      "MACHINE_TYPES_REQUIRED_APP": "Application is required.",
      "MACHINE_TYPES_SUCCESS": "Machine types loaded.",
      "MACHINE_TYPES_TYPE_REQUIRED": "\"type\" field is required.",
      "MACHINE_TYPES_ADDED": "Machine type added.",
      "MACHINE_TYPES_ALREADY_EXISTS": "This type already exists for this application.",
      "MACHINE_TYPES_NOT_FOUND": "Machine type not found.",
      "MACHINE_TYPES_UPDATED": "Machine type updated.",
      "MACHINE_TYPES_DELETED": "Machine type deleted.",
      "MACHINE_TYPES_FORBIDDEN": "Forbidden.",
      "MACHINE_TYPES_SERVER_ERROR": "Server error.",
      "QUESTIONS_REQUIRED_APP": "Application is required.",
      "QUESTIONS_SUCCESS": "Questions loaded.",
      "QUESTIONS_TEXT_REQUIRED": "Text is required.",
      "QUESTIONS_MACHINE_TYPE_REQUIRED": "Machine type is required.",
      "QUESTIONS_INVALID_INTERVENTION": "Invalid intervention type.",
      "QUESTIONS_INVALID_TYPE": "Invalid question type.",
      "QUESTIONS_QCM_OPTIONS_REQUIRED": "For multiple choice, options are required.",
      "QUESTIONS_ADDED": "Question added.",
      "QUESTIONS_NOT_FOUND": "Question not found.",
      "QUESTIONS_DELETED": "Question deleted (ID: {{id}}).",
      "QUESTIONS_UPDATED": "Question updated.",
      "QUESTIONS_SERVER_ERROR": "Server error.",
      "repport_title": "Report Management",
      "repport_tabs_aria": "Report tabs",
      "repport_tab_manage": "Manage",
      "repport_tab_history": "History",
      "repport_new_title_placeholder": "New report title...",
      "repport_new_subtitle_placeholder": "New subtitle...",
      "repport_search_placeholder": "Search by username, QR ID, serial number or technician...",
      "repport_col_date": "Date",
      "repport_col_user": "User",
      "repport_col_technician": "Technician",
      "repport_col_qr": "QR ID",
      "repport_detail_title": "Report details",
      "repport_serial": "Serial number",
      "repport_answers": "Answers",
      "repport_field_question": "Question",
      "repport_field_type": "Type",
      "repport_add_title": "Add new title",
      "repport_title_ph": "Title",
      "repport_add_title_btn": "Add title",
      "repport_loading_titles": "Loading titles…",
      "repport_no_titles": "No titles yet.",
      "repport_toggle_title": "Click to toggle subtitles",
      "repport_delete_title": "Delete this title",
      "repport_loading_subtitles": "Loading subtitles…",
      "repport_no_subtitles": "No subtitles for this title.",
      "repport_toggle_subtitle": "Click to toggle questions",
      "repport_add_subtitle": "Add subtitle",
      "repport_add_subtitle_for_title": "Add subtitle for this title",
      "repport_subtitle_ph": "New subtitle",
      "repport_add_subtitle_btn": "Add subtitle",
      "repport_loading_questions": "Loading questions…",
      "repport_no_questions": "No questions for this subtitle.",
      "repport_add_question": "Add question",
      "repport_add_question_btn": "Add question",
      "repport_question_text_ph": "Question text",
      "repport_qcm_options": "Options (comma separated)",
      "repport_qcm_options_ph": "e.g. \"Low, Medium, High\"",
      "repport_loading_history": "Loading history…",
      "repport_no_submissions": "No submissions found.",
      "repport_filter_label": "Filter by client (username)",
      "repport_filter_ph": "Type a username… (e.g. ehsan)",
      "repport_showing": "Showing",
      "repport_results": "result(s)",
      "repport_row_qr": "QR:",
      "repport_row_serial": "Serial number:",
      "repport_details": "Report details",
      "repport_building": "Building report…",
      "repport_submitted": "Submitted",
      "repport_download_pdf": "Download PDF",
      "repport_pdf_generating": "Generating PDF...",
      "repport_answer": "Answer",
      "repport_unanswered": "Unanswered",
      "repport_no_questions_in_submission": "No questions in this submission.",
      "repport_pdf_title": "Report",
      "repport_pdf_date": "Date",
      "repport_pdf_qr_id": "QR ID",
      "repport_pdf_serial": "Serial Number",
      "repport_pdf_username": "Username",
      "repport_pdf_technician": "Technician",
      "repport_th_question": "Question",
      "repport_th_answer": "Answer",
      "repport_type": "Type",
      "repport_type_open": "Open",
      "repport_type_qcm": "QCM",
      "repport_type_yesno": "Yes / No",
      "repport_popup_blocked": "Popup blocked. Please allow popups to download the PDF.",
      "repport_confirm_delete_title": "Delete title \"{{title}}\" (all its subtitles & questions)?",
      "repport_confirm_delete_subtitle": "Delete subtitle \"{{subtitle}}\" and all its questions?",
      "repport_confirm_delete_question": "Delete this question?",
      "repport_err_app_missing": "Application missing (localStorage).",
      "repport_err_title_required": "Title is required.",
      "repport_err_title_exists": "This title already exists for this application.",
      "repport_err_title_add_failed": "Failed to add title.",
      "repport_err_title_delete_failed": "Failed to delete title.",
      "repport_err_select_title_first": "Select a title first.",
      "repport_err_subtitle_required": "Subtitle is required.",
      "repport_err_subtitle_exists": "This subtitle already exists.",
      "repport_err_subtitle_add_failed": "Failed to add subtitle.",
      "repport_err_subtitle_delete_failed": "Failed to delete subtitle.",
      "repport_err_select_title_subtitle_first": "Select a title and subtitle first.",
      "repport_err_question_text_required": "Question text is required.",
      "repport_err_invalid_question_type": "Invalid question type.",
      "repport_err_qcm_options_required": "For QCM, add at least one option.",
      "repport_question_added": "Question added.",
      "repport_err_question_add_failed": "Failed to add question.",
      "repport_err_question_delete_failed": "Failed to delete question.",
      "repport_title_added": "Title added.",
      "repport_subtitle_added": "Subtitle added.",
      "repport_err_titles_load": "Failed to load titles.",
      "repport_err_subtitles_load": "Failed to load subtitles.",
      "repport_err_questions_load": "Failed to load questions.",
      "repport_err_history_load": "Failed to load history.",
      "repport_err_build_report": "Failed to build report (meta/questions).",
      "repport_err_pdf_download": "Unable to download the PDF right now.",
      "repport_history_app_required": "Application is required.",
      "repport_history_success": "History loaded successfully.",
      "repport_history_db_error": "Database error while loading history.",
      "repport_meta_required_ids": "Question IDs are required.",
      "repport_meta_no_valid_ids": "No valid question IDs provided.",
      "repport_meta_success": "Metadata loaded successfully.",
      "repport_meta_db_error": "Database error while loading metadata.",
      "ur_app_required": "Application parameter is required.",
      "ur_users_loaded": "Users loaded.",
      "ur_server_error": "Server error. Please try again later.",
      "ur_missing_fields": "Missing required fields.",
      "ur_tech_fields_required": "Specialty and sector are required for technicians.",
      "ur_email_exists": "This email is already registered.",
      "ur_username_exists": "This username is already used for this application.",
      "ur_register_success": "User registered successfully.",
      "ur_id_required": "Missing required field: id.",
      "ur_user_not_found": "User not found.",
      "ur_delete_success": "User deleted successfully.",
      "na": "N/A",
      "unknown": "Unknown",
      "client": "Client",
      "technician": "Technician",
      "type": "Type",
      "required": "Required",
      "view": "View",
      "rp_qr": "QR code",
      "no_data": "No data",
      "signature": "Signature",
      "add": "Add",
      "admin_manage_apps_users": "Manage apps and users",
      "admin_users_total": "Total users",
      "application": "Application",
      "auth_login_story_text": "Sign in to access your QR codes, reports, and interventions.",
      "auth_login_story_title": "Welcome back",
      "auth_proof_access": "Controlled access",
      "auth_proof_qr": "Connected machines",
      "auth_proof_reports": "Automatic reports",
      "auth_signup_feature_1": "QR code management",
      "auth_signup_feature_2": "Smart reports",
      "auth_signup_feature_3": "Intervention planning",
      "auth_signup_story_text": "Create your workspace and start digitizing maintenance.",
      "auth_signup_story_title": "Get started with AssistByScan",
      "darkMode": {
        "toggle": "Toggle dark mode"
      },
      "date": "Date",
      "details": "Details",
      "filter": "Filter",
      "forgot_intro": "Enter your email to receive a reset link.",
      "forgot_resend_window": "You can resend an email in {{seconds}}s.",
      "forgot_secure_link": "Secure link sent by email.",
      "forgot_story_title": "Reset your password",
      "help_replace_video": "Replace video",
      "language": "Language",
      "qr_generated_title": "Generated QR codes",
      "reset_success": "Your password has been reset successfully.",
      "rp_popup_blocked": "Popup blocked. Please allow popups.",
      "static_title": "Static pages",
      "theme_dark": "Dark mode",
      "theme_light": "Light mode",
      "urw_add_other": "Add another user",
      "urw_subtitle": "Register users for your application.",
      "urw_success_title": "User registered successfully"
    }
  },
  "ar": {
    "translation": {
      "nav": {
        "features": "الميزات",
        "howItWorks": "كيف يعمل",
        "pricing": "الأسعار",
        "contact": "اتصل بنا",
        "login": "تسجيل الدخول",
        "home": "الرئيسية",
        "register": "تسجيل"
      },
      "hero": {
        "badge": "منصة SaaS · الصيانة الرقمية عبر رمز QR",
        "title": "رقمنة تدخلاتك التقنية بمسح بسيط",
        "subtitle": "كل جهاز مرتبط برمز QR، كل تدخل مسجل، كل تقرير يتم إنشاؤه تلقائياً. منصة ويب + موبايل لمركزية وأتمتة جميع أعمال الصيانة الخاصة بك.",
        "login": "تسجيل الدخول",
        "signup": "إنشاء حساب",
        "discover": "اكتشف المنصة"
      },
      "pricing": {
        "label": "الأسعار",
        "title": "جرّب مجانًا وادفع فقط إذا اقتنعت",
        "subtitle": "ابدأ بالتجربة المجانية، ثم فعّل Pro بدفع بطاقة تجريبي عندما تكون جاهزًا.",
        "noCreditCard": "دفع بطاقة تجريبي",
        "noCommitment": "بدون التزام",
        "cancelAnytime": "يمكن الإلغاء في أي وقت",
        "payment": {
          "demoBadge": "دفع بطاقة تجريبي",
          "title": "فعّل اشتراك Pro",
          "subtitle": "أكمل التفعيل ببطاقة اختبار. لا يتم إجراء أي خصم حقيقي في هذا الوضع.",
          "cardName": "الاسم على البطاقة",
          "cardNamePlaceholder": "محمد علي",
          "cardNumber": "رقم البطاقة",
          "expiry": "تاريخ الانتهاء",
          "cvc": "CVC",
          "demoNotice": "وضع الدفع غير نشط: هذه العملية تحاكي الدفع وتفعّل الاشتراك لتطبيقك.",
          "mockOnly": "عملية محاكاة",
          "noCharge": "لا يوجد خصم بنكي",
          "activateNow": "تفعيل فوري",
          "missingApp": "سجّل الدخول بتطبيقك قبل تفعيل الاشتراك.",
          "invalid": "يرجى إكمال بيانات بطاقة الاختبار.",
          "processing": "جارٍ التفعيل…",
          "payButton": "تفعيل الاشتراك",
          "success": "تم تفعيل الاشتراك. يمكنك الآن إنشاء المزيد من رموز QR.",
          "backToQr": "العودة إلى مولّد QR"
        },
        "plans": [
          {
            "name": "تجربة مجانية",
            "price": "€0",
            "period": "لمدة 14 يومًا",
            "description": "جرّب جميع الميزات بدون التزام أو بطاقة ائتمان.",
            "badge": "14 يومًا مجانًا",
            "cta": "ابدأ التجربة",
            "features": [
              "وصول كامل للمنصة",
              "5 رموز QR مضمّنة",
              "حتى 5 مستخدمين",
              "نماذج ديناميكية",
              "تقارير PDF",
              "دعم عبر البريد الإلكتروني"
            ]
          },
          {
            "name": "Pro",
            "price": "€149",
            "period": "/شهريًا",
            "description": "بعد انتهاء التجربة، قم بالترقية إلى Pro للاستمرار بدون انقطاع.",
            "badge": "",
            "cta": "اختر خطة Pro",
            "features": [
              "كل ما في الخطة المجانية",
              "مستخدمون غير محدودين",
              "جدولة متقدمة",
              "إشعارات فورية",
              "وصول API",
              "دعم مميز",
              "تصدير PDF متقدم"
            ]
          },
          {
            "name": "Enterprise",
            "price": "مخصص",
            "period": "",
            "description": "للشركات الكبيرة ذات الاحتياجات الخاصة والمتعددة المواقع.",
            "badge": "",
            "cta": "اتصل بنا",
            "features": [
              "كل ما في Pro",
              "SSO و SAML",
              "اتفاقية SLA مضمونة",
              "مدير حساب مخصص",
              "نشر داخلي",
              "تدريب في الموقع"
            ]
          }
        ]
      },
      "features": {
        "badge": "الميزات",
        "title": "كل ما تحتاجه لرقمنة الصيانة الخاصة بك",
        "subtitle": "منصة شاملة تغطي كل خطوة، من مسح رمز QR إلى تصدير التقرير النهائي.",
        "qrcodes": {
          "title": "رموز QR ذكية",
          "desc": "كل جهاز مرتبط برمز QR فريد. مسح بسيط يفتح النموذج المناسب للمعدات وسياق التدخل."
        },
        "forms": {
          "title": "نماذج ديناميكية",
          "desc": "إنشاء استبيانات هرمية (عناوين، عناوين فرعية، أسئلة) بإجابات حرة، اختيارات متعددة أو نعم/لا، مناسبة لكل نوع من أنواع التدخل."
        },
        "planning": {
          "title": "تخطيط ذكي",
          "desc": "يقترح النظام تلقائياً الفترات المتاحة بناءً على جداول كل فني، أيام العمل والعطل الرسمية."
        },
        "reports": {
          "title": "تقارير آلية",
          "desc": "عند اكتمال التدخل، يتم إنشاء تقرير منظم مع جميع البيانات المجمعة، قابل للعرض والتصدير بصيغة PDF."
        },
        "notifications": {
          "title": "إشعارات في الوقت الفعلي",
          "desc": "يتم إخطار المسؤولين والفنيين والعملاء في كل خطوة: طلب جديد، تعيين، تدخل مكتمل."
        },
        "roles": {
          "title": "إدارة متعددة الأدوار",
          "desc": "إدارة فريقك بأدوار متميزة (مسؤول، فني، عميل) وصلاحيات مناسبة لكل ملف تعريف."
        },
        "mobile": {
          "title": "تطبيق ميداني للجوال",
          "desc": "يعمل الفنيون والمستخدمون من تطبيق Android: المسح، التشخيص، الإدخال والإرسال مباشرة في الميدان."
        },
        "languages": {
          "title": "دعم متعدد اللغات",
          "desc": "تتغير الواجهة والرسائل ديناميكياً لتتناسب مع لغة كل مستخدم من خلال نظام ترجمة من جانب الخادم."
        },
        "history": {
          "title": "السجل والتصفية المتقدمة",
          "desc": "العثور على أي تدخل سابق باستخدام فلاتر حسب المستخدم، الجهاز، الفني، التاريخ والحالة."
        }
      },
      "steps": {
        "badge": "كيف يعمل",
        "title": "من المسح إلى التقرير في 5 خطوات",
        "subtitle": "عملية سلسة وآلية، من الإعداد الأولي إلى إنشاء التقرير النهائي.",
        "configure": {
          "title": "تكوين الأجهزة والنماذج الخاصة بك",
          "desc": "من الواجهة الإلكترونية، يحدد المسؤول أنواع الأجهزة وينشئ نماذج هرمية بأسئلة مناسبة لكل سياق تدخل."
        },
        "qrcode": {
          "title": "إنشاء ونشر رموز QR الخاصة بك",
          "desc": "يتلقى كل جهاز رمز QR فريد. اطبعها وضعها على أجهزتك لتجعلها قابلة للوصول الفوري."
        },
        "scan": {
          "title": "المسح والتشخيص والإرسال",
          "desc": "يقوم المستخدم بمسح رمز QR من تطبيق الجوال، ويملأ النموذج المناسب (التشخيص، الطلب المباشر) ويرسل طلبه في ثوانٍ."
        },
        "planning": {
          "title": "التخطيط والتعيين التلقائي",
          "desc": "يحدد النظام الفنيين المتاحين بناءً على جداولهم ومهاراتهم وقيودهم، ثم يقترح تلقائياً فترة تدخل."
        },
        "report": {
          "title": "التدخل والتقرير المُنشأ",
          "desc": "يتدخل الفني، ويكمل بيانات الميدان. يتم إنشاء تقرير منظم تلقائياً، قابل للعرض عبر الإنترنت والتصدير بصيغة PDF."
        }
      },
      "showcase": {
        "badge": "المنتج",
        "title": "ويب + موبايل، منصة واحدة",
        "subtitle": "يقوم المسؤول بتكوين والإشراف من لوحة تحكم الويب. يقوم الفنيون والمستخدمون بالمسح والتشخيص والتدخل من تطبيق Android للجوال."
      },
      "benefits": {
        "badge": "المزايا",
        "title": "نتائج ملموسة من الشهر الأول",
        "time": {
          "title": "توفير الوقت",
          "desc": "لا حاجة للبحث في السجلات أو ملء النماذج الورقية. يتم تتبع كل تدخل تلقائياً من المسح إلى التقرير.",
          "stat": "وقت المعالجة"
        },
        "planning": {
          "title": "تدخلات أفضل تخطيطاً",
          "desc": "يأخذ محرك التخطيط الذكي في الاعتبار التوفر الفعلي، والعطل الرسمية، ومهارات كل فني.",
          "stat": "مزيد من التدخلات/اليوم"
        },
        "paperless": {
          "title": "صفر ورق، صفر خسارة",
          "desc": "تحل النماذج الديناميكية محل الدفاتر. جميع البيانات منظمة ومخزنة ويمكن الوصول إليها في أي وقت من أي جهاز.",
          "stat": "وثيقة ورقية"
        },
        "data": {
          "title": "بيانات مركزية",
          "desc": "تاريخ كامل للتدخلات، تقارير قابلة للتصدير بصيغة PDF، تصفية متقدمة حسب الفني، الجهاز، التاريخ أو الحالة - كل شيء في مكان واحد.",
          "stat": "إمكانية التتبع"
        }
      },
      "cases": {
        "badge": "حالات الاستخدام",
        "title": "متكيف مع قطاعك",
        "subtitle": "مهما كان مجالك، AssistByScan يتكيف مع عمليات الصيانة الخاصة بك.",
        "industry": {
          "title": "الصناعة",
          "desc": "الصيانة الوقائية والتصحيحية على خطوط الإنتاج. كل جهاز محدد برمز QR، كل تدخل مسجل."
        },
        "services": {
          "title": "خدمات الصيانة",
          "desc": "إدارة متعددة العملاء والمواقع. خطط وعين وتتبع تدخلات فنييك في الوقت الفعلي."
        },
        "retail": {
          "title": "البيع بالتجزئة والتجارة",
          "desc": "تتبع المعدات في المتاجر (تكييف الهواء، نقاط البيع، الإضاءة) مع طلبات تدخل بمسحة واحدة."
        },
        "facility": {
          "title": "إدارة المرافق",
          "desc": "إدارة فنية للمباني والمساحات المشتركة مع تتبع كامل وتقارير آلية."
        }
      },
      "footer": {
        "contact": "اتصل بنا",
        "privacy": "سياسة الخصوصية",
        "description": "منصة صيانة رقمية عبر رمز QR.",
        "rights": "جميع الحقوق محفوظة."
      },
      "qr_app_not_found": "التطبيق غير موجود.",
      "qr_limit_reached": "لقد وصل هذا التطبيق إلى الحد الأقصى وهو 5 رموز QR في الخطة المجانية.",
      "qr_limit_exceeded": "هذا الطلب يتجاوز عدد رموز QR المتبقية المسموح بها في الخطة المجانية.",
      "qr_limit_dialog_eyebrow": "الخطة المجانية",
      "qr_limit_dialog_title": "تم الوصول إلى حد الخطة المجانية",
      "qr_limit_dialog_usage_label": "استخدام رموز QR",
      "qr_limit_dialog_usage": "تم استخدام {{used}}/{{limit}} من رموز QR. المتبقي: {{remaining}}.",
      "qr_limit_dialog_benefit_qr": "رموز QR غير محدودة لمعداتك",
      "qr_limit_dialog_benefit_team": "إدارة الفريق والوصول عبر الويب والجوال",
      "qr_limit_dialog_benefit_reports": "تقارير تدخل مركزية",
      "qr_limit_dialog_subscribe": "اشترك الآن",
      "subscription_app_missing": "التطبيق مفقود لتفعيل الاشتراك.",
      "subscription_plan_invalid": "خطة الاشتراك غير صالحة.",
      "subscription_mock_success": "تم تفعيل الاشتراك.",
      "subscription_mock_error": "فشل تفعيل الاشتراك.",
      "download_app": "تحميل التطبيق",
      "assist_by_scan": "ASSIST BY SCAN",
      "login_title": "تسجيل الدخول",
      "email": "البريد الإلكتروني",
      "password": "كلمة المرور",
      "login": "دخول",
      "forgot_password": "نسيت كلمة المرور؟",
      "no_account": "ليس لديك حساب؟",
      "signup": "إنشاء حساب",
      "nav_login": "دخول",
      "nav_signup": "إنشاء حساب",
      "msg_enter_email_password": "يرجى إدخال البريد الإلكتروني وكلمة المرور.",
      "msg_invalid_email": "يرجى إدخال بريد إلكتروني صالح.",
      "msg_unknown_error": "خطأ غير معروف.",
      "msg_server_or_network": "خطأ في الخادم أو الشبكة.",
      "msg_incorrect_email_or_password": "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
      "msg_login_success": "تم تسجيل الدخول بنجاح!",
      "admin_dashboard": "لوحة تحكم المشرف",
      "admin_loading_users": "جاري تحميل المستخدمين...",
      "admin_error_fetch_users": "حدث خطأ أثناء جلب المستخدمين.",
      "admin_error_delete_user": "حدث خطأ أثناء حذف المستخدم.",
      "admin_confirm_delete": "هل أنت متأكد أنك تريد حذف هذا المستخدم؟",
      "admin_th_id": "المعرّف",
      "admin_th_application": "التطبيق",
      "admin_th_type": "النوع",
      "admin_th_email": "البريد الإلكتروني",
      "admin_th_city": "المدينة",
      "admin_th_country": "الدولة",
      "admin_th_status": "الحالة",
      "admin_th_role": "الدور",
      "admin_th_qr_count": "عدد رموز QR",
      "admin_th_actions": "الإجراءات",
      "status_active": "نشط",
      "status_not_active": "غير نشط",
      "delete": "حذف",
      "logout": "تسجيل الخروج",
      "qr_generator": "مولّد QR",
      "questions": "الأسئلة",
      "report": "التقرير",
      "static_page": "صفحة ثابتة",
      "help_center": "مركز المساعدة",
      "register_admin": "تسجيل مسؤول",
      "register_user": "تسجيل مستخدم",
      "contact_us": "اتصل بنا",
      "contact_title": "اتصل بنا",
      "contact_direct_title": "أو تواصل معنا مباشرة",
      "contact_phone_label": "الهاتف",
      "contact_subtitle": "هل تحتاج إلى مساعدة؟ أرسل لنا رسالة وسنرد عليك في أقرب وقت ممكن.",
      "contact_name": "اسمك",
      "contact_email": "بريدك الإلكتروني",
      "contact_subject": "الموضوع",
      "contact_message_placeholder": "اكتب رسالتك...",
      "contact_send": "إرسال الرسالة",
      "contact_sending": "جارٍ الإرسال...",
      "contact_reset": "إعادة تعيين",
      "contact_all_required": "جميع الحقول مطلوبة.",
      "contact_message_short": "الرسالة قصيرة جداً (الحد الأدنى 10 أحرف).",
      "contact_success": "تم إرسال الرسالة بنجاح.",
      "contact_failed": "فشل إرسال الرسالة.",
      "contact_error_general": "حدث خطأ أثناء الإرسال. حاول مرة أخرى.",
      "contact_email_label": "البريد الإلكتروني",
      "contact_name_required": "الاسم مطلوب.",
      "contact_subject_required": "الموضوع مطلوب.",
      "contact_message_required": "الرسالة مطلوبة.",
      "reset_title": "إنشاء كلمة مرور جديدة",
      "reset_verifying": "جارٍ التحقق من الرابط…",
      "reset_new_password": "كلمة مرور جديدة",
      "reset_confirm_password": "تأكيد كلمة المرور",
      "reset_saving": "جارٍ الحفظ…",
      "save": "حفظ",
      "show": "إظهار",
      "hide": "إخفاء",
      "reset_remember": "هل تذكرت؟",
      "reset_fill_both_fields": "يرجى ملء الحقلين.",
      "reset_pwd_rules": "كلمة المرور ضعيفة: 8+ أحرف، حرف كبير، رقم، ورمز خاص مطلوب.",
      "reset_missing_link": "رابط إعادة التعيين غير موجود.",
      "reset_missing_link_long": "رابط إعادة التعيين مفقود. يرجى استخدام الرابط الذي وصل عبر البريد الإلكتروني.",
      "reset_success_redirect": "تم تحديث كلمة المرور. جارٍ التحويل…",
      "reset_err_invalid_or_expired": "الرابط غير صالح أو منتهي.",
      "reset_err_unexpected": "خطأ غير متوقع.",
      "reset_err_server": "خطأ في الخادم.",
      "reset_err_weak_password": "كلمة المرور ضعيفة.",
      "reset_err_password_mismatch": "كلمتا المرور غير متطابقتين.",
      "reset_err_expired": "انتهت صلاحية الرابط. اطلب رابطاً جديداً.",
      "reset_err_missing_fields": "يرجى ملء جميع الحقول.",
      "reset_err_invalid_or_used": "الرابط غير صالح أو تم استخدامه بالفعل.",
      "reset_processed": "إذا كان الحساب موجودًا، فقد تمت معالجة إعادة تعيين كلمة المرور.",
      "download_title": "تثبيت Assist by Scan",
      "download_mobile_text": "اضغط على الزر أدناه لتنزيل التطبيق:",
      "download_desktop_text": "امسح رمز QR باستخدام جهاز Android لتنزيل التطبيق:",
      "download_apk": "تنزيل التطبيق (APK)",
      "download_feature_android": "Android ميداني",
      "download_feature_scan": "مسح QR فوري",
      "download_feature_secure": "وصول آمن",
      "download_holo_scan_fast": "مسح سريع",
      "download_holo_apk_secure": "APK آمن",
      "download_tap": "اضغط",
      "download_scan": "امسح",
      "editable_placeholder": "عدّل نص {{title}} هنا...",
      "editable_load_failed": "تعذر تحميل نص \"{{title}}\".",
      "editable_text_empty": "لا يمكن أن يكون النص فارغاً.",
      "editable_update_success": "تم تحديث نص \"{{title}}\" بنجاح.",
      "editable_update_failed": "فشل التحديث.",
      "edit": "تعديل",
      "saving": "جارٍ الحفظ…",
      "cancel": "إلغاء",
      "forgot_title": "نسيت كلمة المرور",
      "forgot_email_required": "البريد الإلكتروني مطلوب.",
      "forgot_email_sent": "إذا كان الحساب موجودًا، فقد تم إرسال رابط إعادة التعيين.",
      "forgot_send_link": "إرسال رابط إعادة التعيين",
      "forgot_sending": "جارٍ الإرسال…",
      "forgot_resend_in": "إعادة الإرسال خلال {{seconds}} ثانية",
      "forgot_remember": "هل تذكرت كلمة المرور؟",
      "help_title": "إدارة مهام المساعدة",
      "loading": "جارٍ التحميل...",
      "help_load_failed": "فشل تحميل المهام.",
      "help_add_new": "إضافة مهمة جديدة",
      "help_new_title_ph": "عنوان المهمة الجديدة",
      "help_new_content_ph": "محتوى المهمة الجديدة",
      "help_add_btn": "إضافة",
      "help_add_required": "العنوان والمحتوى مطلوبان.",
      "help_app_missing": "التطبيق غير موجود (localStorage userApplication).",
      "help_add_success": "تمت إضافة مهمة جديدة.",
      "help_add_failed": "فشل الإضافة:",
      "help_item_empty": "لا يمكن أن يكون العنوان والمحتوى فارغين.",
      "help_item_saved": "تم حفظ المهمة بنجاح.",
      "help_item_save_failed": "فشل الحفظ:",
      "help_confirm_delete_task": "هل أنت متأكد أنك تريد حذف هذه المهمة؟",
      "help_deleted": "تم الحذف.",
      "help_delete_failed": "فشل الحذف.",
      "help_toggle_title": "انقر لإظهار/إخفاء الوصف",
      "help_upload_video": "رفع فيديو",
      "help_change_video": "تغيير الفيديو",
      "help_video_badge": "فيديو",
      "help_delete_video": "حذف الفيديو",
      "help_default_1_title": "كيف يمكن مسح رمز QR؟",
      "help_default_1_content": "افتح تطبيق الهاتف، اضغط زر المسح، ثم وجّه الكاميرا نحو رمز QR الموجود على الآلة.",
      "help_default_2_title": "كيف يمكن إنشاء تقرير؟",
      "help_default_2_content": "بعد إكمال التدخل، اضغط على إنشاء التقرير لإنشاء ملف PDF تلقائي بكل البيانات.",
      "help_default_3_title": "كيف يمكن إضافة مستخدم؟",
      "help_default_3_content": "اذهب إلى تسجيل مستخدم، ثم املأ النموذج بالبريد الإلكتروني واسم المستخدم والدور.",
      "help_choose_video": "يرجى اختيار ملف فيديو.",
      "help_video_uploaded": "تم رفع الفيديو.",
      "help_upload_failed": "فشل الرفع:",
      "help_tasks_required_params": "معامل التطبيق مفقود.",
      "help_tasks_not_found": "لم يتم العثور على مهام مساعدة.",
      "help_tasks_success": "تم تحميل المهام.",
      "help_tasks_server_error": "خطأ في الخادم أثناء تحميل المهام.",
      "help_task_not_found": "مهمة المساعدة غير موجودة.",
      "help_video_missing_field": "حقل 'video' مفقود (multipart/form-data).",
      "help_no_file_selected": "لم يتم اختيار ملف.",
      "help_invalid_video_format": "صيغة الفيديو غير صالحة. الصيغ المسموحة: mp4, webm, ogg, mov, m4v.",
      "pwd_success_title": "تم تغيير كلمة المرور",
      "pwd_success_main": "تم تغيير كلمة المرور بنجاح.",
      "pwd_success_hint": "يمكنك إغلاق هذه الصفحة وإعادة فتح التطبيق لتسجيل الدخول.",
      "qr_title": "مولّد رموز QR",
      "qr_views_aria": "عرض QR",
      "qr_tab_generate": "إنشاء رموز QR",
      "qr_tab_history": "عرض السجل",
      "qr_count_aria": "عدد رموز QR المراد إنشاؤها",
      "qr_size_aria": "حجم رمز QR بالسنتيمتر",
      "qr_size_option": "{{size}} سم",
      "generate": "إنشاء",
      "qr_generating": "جارٍ الإنشاء…",
      "qr_generation_progress": "جارٍ إنشاء الرموز… ⏳",
      "qr_download_a4_pdf": "تنزيل PDF A4",
      "qr_download_pdf": "تنزيل PDF",
      "prev": "→ السابق",
      "next": "التالي ←",
      "page": "صفحة",
      "of": "من",
      "qr_loading_history": "جارٍ تحميل السجل… ⏳",
      "qr_no_history": "لا توجد رموز QR في السجل.",
      "qr_status_active": "نشط",
      "qr_status_not_active": "غير نشط",
      "print": "طباعة",
      "qr_print_title": "طباعة QR",
      "qr_alt": "رمز QR لـ {{code}}",
      "qr_confirm_delete": "هل تريد حقاً حذف رمز QR هذا؟",
      "qr_err_invalid_number": "يرجى إدخال رقم صالح (≥ 1).",
      "qr_err_generate": "حدث خطأ أثناء إنشاء رموز QR. حاول مرة أخرى.",
      "qr_err_delete": "حدث خطأ أثناء حذف رمز QR.",
      "qr_err_history": "حدث خطأ أثناء تحميل السجل. حاول مرة أخرى.",
      "qr_err_pdf_single": "فشل تنزيل PDF. تحقق من CORS / الوصول للصورة.",
      "qr_err_pdf_export": "فشل تصدير PDF. تحقق من CORS / الوصول للصورة.",
      "qr_app_missing": "التطبيق غير موجود.",
      "qr_generate_success": "تم إنشاء رموز QR.",
      "qr_history_success": "تم تحميل السجل.",
      "qr_not_found": "رمز QR غير موجود.",
      "qr_delete_active_forbidden": "لا يمكن حذف رمز QR نشط.",
      "qr_delete_success": "تم حذف رمز QR بنجاح.",
      "qf_type_required": "نوع الآلة مطلوب.",
      "qf_type_added": "تم إضافة نوع الآلة.",
      "qf_type_cannot_empty": "لا يمكن أن يكون النوع فارغاً.",
      "qf_type_updated": "تم تحديث نوع الآلة.",
      "qf_confirm_delete_type": "حذف نوع الآلة هذا (وجميع أسئلته)؟",
      "qf_type_deleted": "تم حذف نوع الآلة.",
      "qf_app_missing": "التطبيق غير موجود (localStorage userApplication).",
      "qf_select_machine_type_placeholder": "— اختر نوع الآلة —",
      "qf_type_open": "مفتوح",
      "qf_type_qcm": "اختيار متعدد",
      "qf_type_yesno": "نعم / لا",
      "qf_qcm_options_ph": "خيارات مفصولة بفواصل: مثال \"منخفض, متوسط, مرتفع\"",
      "qf_question_ph": "اكتب سؤالك هنا…",
      "qf_it_report_repair": "تقرير إصلاح",
      "qf_it_ask_repair": "طلب تدخل",
      "qf_filter_ask_repair": "طلب إصلاح",
      "qf_question_added": "تمت إضافة السؤال بنجاح.",
      "qf_questions_list_title": "قائمة الأسئلة",
      "qf_filters_aria": "فلاتر الأسئلة",
      "qf_delete_selected": "حذف المحدد",
      "qf_question_deleted": "تم حذف السؤال.",
      "qf_question_deleted_with_id": "تم حذف السؤال {{id}}.",
      "qf_question_updated": "تم تحديث السؤال.",
      "qf_confirm_delete_question": "حذف هذا السؤال؟",
      "qf_options": "الخيارات",
      "qf_type_badge": "النوع",
      "qf_machine_types_title": "أنواع الآلات",
      "qf_add_type_ph": "إضافة نوع آلة…",
      "qf_add_type_btn": "إضافة",
      "qf_add_question_title": "إضافة سؤال",
      "qf_filter_all": "التصفية: الكل",
      "qf_col_question": "السؤال",
      "qf_col_type": "النوع",
      "qf_col_machine": "الآلة",
      "qf_col_intervention": "نوع التدخل",
      "qf_options_placeholder": "الخيار 1، الخيار 2، الخيار 3",
      "qf_delete_selected_success": "تم حذف {{count}} سؤال(أسئلة).",
      "qf_select_intervention": "يرجى اختيار نوع التدخل.",
      "qf_select_machine_type": "يرجى اختيار نوع آلة.",
      "qf_text_required": "يرجى إدخال السؤال.",
      "qf_text_cannot_empty": "لا يمكن أن يكون النص فارغًا.",
      "qf_invalid_question_type": "نوع السؤال غير صالح.",
      "qf_qcm_need_options": "لسؤال الاختيار من متعدد يجب إدخال خيارات.",
      "qf_select_at_least_one": "اختر سؤالًا واحدًا على الأقل.",
      "qf_err_load_types": "تعذر تحميل أنواع الآلات.",
      "qf_err_load_questions": "تعذر تحميل الأسئلة.",
      "qf_type_add_failed": "فشل إضافة نوع الآلة.",
      "qf_type_update_failed": "فشل تحديث نوع الآلة.",
      "qf_type_delete_failed": "فشل حذف نوع الآلة.",
      "qf_type_delete_conflict": "لا يمكن الحذف: هذا النوع مستخدم بالفعل.",
      "qf_question_add_failed": "فشل إضافة السؤال.",
      "qf_question_update_failed": "فشل تحديث السؤال.",
      "qf_question_delete_failed": "فشل حذف السؤال.",
      "qf_delete_selected_failed": "فشل حذف المحدد.",
      "qf_select_question_aria": "تحديد السؤال {{id}}",
      "close": "إغلاق",
      "choose_file": "اختيار ملف",
      "no_file_selected": "لم يتم اختيار ملف",
      "uploading": "جارٍ الرفع...",
      "upload": "رفع",
      "replace": "استبدال",
      "help_video_choose_hint": "اختر فيديو من جهازك.",
      "help_video_replace_hint": "اختر فيديو جديداً لاستبدال الفيديو الحالي.",
      "signup_title": "إنشاء حساب",
      "city": "المدينة",
      "country": "الدولة",
      "signup_confirm_password": "تأكيد كلمة المرور",
      "signup_loading": "جارٍ إنشاء الحساب…",
      "signup_have_account": "لديك حساب بالفعل؟",
      "show_password": "إظهار كلمة المرور",
      "hide_password": "إخفاء كلمة المرور",
      "signup_fix_errors": "يرجى تصحيح الأخطاء المميزة.",
      "signup_success_generic": "إذا كان البريد صحيحاً، تم إرسال رابط التفعيل.",
      "signup_err_email_required": "البريد الإلكتروني مطلوب.",
      "signup_err_city_required": "المدينة مطلوبة.",
      "signup_err_application_required": "التطبيق مطلوب.",
      "signup_err_country_required": "الدولة مطلوبة.",
      "signup_err_password_required": "كلمة المرور مطلوبة.",
      "signup_err_password_weak": "٨+ أحرف، حرف كبير، رقم، ورمز خاص.",
      "signup_err_confirm_required": "يرجى تأكيد كلمة المرور.",
      "signup_err_password_mismatch": "كلمتا المرور غير متطابقتين.",
      "signup_err_invalid_value": "قيمة غير صحيحة.",
      "signup_err_validation": "أخطاء في التحقق.",
      "signup_err_generic": "حدث خطأ أثناء التسجيل. حاول لاحقاً.",
      "signup_err_email_exists": "البريد الإلكتروني مسجّل مسبقاً.",
      "signup_err_application_exists": "التطبيق مسجل بالفعل.",
      "static_about": "من نحن",
      "static_terms": "شروط الاستخدام",
      "static_privacy": "سياسة الخصوصية",
      "add_user": "إضافة مستخدم",
      "username": "اسم المستخدم",
      "select_role": "اختر الدور",
      "role": "الدور",
      "role_technician": "فني",
      "role_client": "عميل",
      "specialty": "التخصص",
      "sector": "القطاع",
      "register": "تسجيل",
      "registering": "جارٍ التسجيل...",
      "users_list": "قائمة المستخدمين",
      "status": "الحالة",
      "actions": "الإجراءات",
      "active": "نشط",
      "not_active": "غير نشط",
      "no_users": "لا يوجد مستخدمون.",
      "all_fields_required": "جميع الحقول مطلوبة.",
      "tech_fields_required": "التخصص والقطاع مطلوبان للفنيين.",
      "user_registered": "تم تسجيل المستخدم بنجاح.",
      "registration_error": "خطأ في التسجيل.",
      "registration_failed": "فشل في التسجيل.",
      "confirm_delete_user": "هل أنت متأكد أنك تريد حذف هذا المستخدم؟",
      "user_deleted": "تم حذف المستخدم بنجاح.",
      "delete_failed": "فشل في حذف المستخدم.",
      "delete_error": "حدث خطأ أثناء الحذف.",
      "user_load_failed": "فشل في تحميل المستخدمين.",
      "user_fetch_error": "حدث خطأ أثناء جلب المستخدمين.",
      "urw_title": "إضافة مسؤول جديد",
      "urw_ph_email": "البريد الإلكتروني *",
      "urw_ph_application": "التطبيق *",
      "urw_role_legend": "اختر الدور:",
      "urw_role_user": "مستخدم",
      "urw_role_admin": "مسؤول",
      "urw_type_legend": "اختر النوع:",
      "urw_type_scan": "مسح",
      "urw_type_direct": "مباشر",
      "urw_type_both": "كلاهما",
      "urw_register": "تسجيل",
      "urw_registering": "جارٍ التسجيل…",
      "urw_fill_required": "يرجى تعبئة جميع الحقول المطلوبة.",
      "urw_success": "تم تسجيل المستخدم بنجاح!",
      "urw_failed": "فشل التسجيل.",
      "urw_db_error": "البريد الإلكتروني مسجل بالفعل أو خطأ في قاعدة البيانات.",
      "verify_verifying_title": "جارٍ التحقق…",
      "verify_verifying_text": "يرجى الانتظار قليلاً.",
      "verify_success_title": "تم بنجاح",
      "verify_success_register": "تم التحقق من بريدك الإلكتروني.",
      "verify_success_change_email": "تم تغيير بريدك الإلكتروني بنجاح.",
      "verify_success_delete": "تم حذف حسابك.",
      "verify_fail_title": "فشل التحقق",
      "verify_missing_token": "رمز التحقق مفقود.",
      "verify_invalid": "الرابط غير صالح أو منتهي الصلاحية.",
      "verify_failed": "فشل التحقق.",
      "verify_go_login": "الانتقال إلى تسجيل الدخول",
      "verify_back_login": "العودة إلى تسجيل الدخول",
      "VERIFY_TOKEN_MISSING": "رمز التحقق مفقود.",
      "VERIFY_TOKEN_INVALID_OR_USED": "الرابط غير صالح أو تم استخدامه بالفعل.",
      "VERIFY_TOKEN_EXPIRED": "انتهت صلاحية الرابط.",
      "VERIFY_INVALID_PAYLOAD": "بيانات التحقق غير صالحة.",
      "verify_invalid_flow": "مسار التحقق غير صالح.",
      "verify_db_error": "خطأ في الخادم/قاعدة البيانات.",
      "verify_new_email_already_in_use": "عنوان البريد الإلكتروني الجديد مستخدم بالفعل.",
      "VERIFY_REGISTER_SUCCESS": "تم التحقق من بريدك الإلكتروني بنجاح.",
      "verify_change_email_success": "تم تغيير بريدك الإلكتروني بنجاح.",
      "verify_delete_account_success": "تم حذف حسابك بنجاح.",
      "msg_email_required": "البريد الإلكتروني مطلوب.",
      "msg_no_data_received": "لم يتم استلام بيانات.",
      "msg_password_required": "كلمة المرور مطلوبة.",
      "msg_database_error": "خطأ في قاعدة البيانات.",
      "msg_processing_error": "خطأ أثناء المعالجة.",
      "admin_user_not_found": "المستخدم غير موجود.",
      "admin_user_deleted_success": "تم حذف المستخدم بنجاح.",
      "MACHINE_TYPES_REQUIRED_APP": "التطبيق مطلوب.",
      "MACHINE_TYPES_SUCCESS": "تم تحميل أنواع الآلات.",
      "MACHINE_TYPES_TYPE_REQUIRED": "حقل \"type\" مطلوب.",
      "MACHINE_TYPES_ADDED": "تمت إضافة نوع الآلة.",
      "MACHINE_TYPES_ALREADY_EXISTS": "هذا النوع موجود بالفعل لهذا التطبيق.",
      "MACHINE_TYPES_NOT_FOUND": "نوع الآلة غير موجود.",
      "MACHINE_TYPES_UPDATED": "تم تحديث نوع الآلة.",
      "MACHINE_TYPES_DELETED": "تم حذف نوع الآلة.",
      "MACHINE_TYPES_FORBIDDEN": "غير مصرح.",
      "MACHINE_TYPES_SERVER_ERROR": "خطأ في الخادم.",
      "QUESTIONS_REQUIRED_APP": "التطبيق مطلوب.",
      "QUESTIONS_SUCCESS": "تم تحميل الأسئلة.",
      "QUESTIONS_TEXT_REQUIRED": "النص مطلوب.",
      "QUESTIONS_MACHINE_TYPE_REQUIRED": "نوع الآلة مطلوب.",
      "QUESTIONS_INVALID_INTERVENTION": "نوع التدخل غير صالح.",
      "QUESTIONS_INVALID_TYPE": "نوع السؤال غير صالح.",
      "QUESTIONS_QCM_OPTIONS_REQUIRED": "لسؤال الاختيار من متعدد الخيارات مطلوبة.",
      "QUESTIONS_ADDED": "تمت إضافة السؤال.",
      "QUESTIONS_NOT_FOUND": "السؤال غير موجود.",
      "QUESTIONS_DELETED": "تم حذف السؤال (المعرف: {{id}}).",
      "QUESTIONS_UPDATED": "تم تحديث السؤال.",
      "QUESTIONS_SERVER_ERROR": "خطأ في الخادم.",
      "repport_title": "إدارة التقارير",
      "repport_tabs_aria": "تبويبات التقارير",
      "repport_tab_manage": "إدارة",
      "repport_tab_history": "السجل",
      "repport_new_title_placeholder": "عنوان تقرير جديد...",
      "repport_new_subtitle_placeholder": "عنوان فرعي جديد...",
      "repport_search_placeholder": "ابحث باسم المستخدم أو QR ID أو الرقم التسلسلي أو الفني...",
      "repport_col_date": "التاريخ",
      "repport_col_user": "المستخدم",
      "repport_col_technician": "الفني",
      "repport_col_qr": "QR ID",
      "repport_detail_title": "تفاصيل التقرير",
      "repport_serial": "الرقم التسلسلي",
      "repport_answers": "الإجابات",
      "repport_field_question": "السؤال",
      "repport_field_type": "النوع",
      "repport_add_title": "إضافة عنوان جديد",
      "repport_title_ph": "العنوان",
      "repport_add_title_btn": "إضافة العنوان",
      "repport_loading_titles": "جارٍ تحميل العناوين…",
      "repport_no_titles": "لا توجد عناوين بعد.",
      "repport_toggle_title": "انقر لإظهار/إخفاء العناوين الفرعية",
      "repport_delete_title": "حذف هذا العنوان",
      "repport_loading_subtitles": "جارٍ تحميل العناوين الفرعية…",
      "repport_no_subtitles": "لا توجد عناوين فرعية لهذا العنوان.",
      "repport_toggle_subtitle": "انقر لإظهار/إخفاء الأسئلة",
      "repport_add_subtitle": "إضافة عنوان فرعي",
      "repport_add_subtitle_for_title": "إضافة عنوان فرعي لهذا العنوان",
      "repport_subtitle_ph": "عنوان فرعي جديد",
      "repport_add_subtitle_btn": "إضافة العنوان الفرعي",
      "repport_loading_questions": "جارٍ تحميل الأسئلة…",
      "repport_no_questions": "لا توجد أسئلة لهذا العنوان الفرعي.",
      "repport_add_question": "إضافة سؤال",
      "repport_add_question_btn": "إضافة السؤال",
      "repport_question_text_ph": "نص السؤال",
      "repport_qcm_options": "الخيارات (مفصولة بفواصل)",
      "repport_qcm_options_ph": "مثال: \"منخفض، متوسط، مرتفع\"",
      "repport_loading_history": "جارٍ تحميل السجل…",
      "repport_no_submissions": "لم يتم العثور على أي نتائج.",
      "repport_filter_label": "التصفية حسب العميل (اسم المستخدم)",
      "repport_filter_ph": "اكتب اسم المستخدم… (مثال: ehsan)",
      "repport_showing": "عرض",
      "repport_results": "نتيجة/نتائج",
      "repport_row_qr": "QR:",
      "repport_row_serial": "الرقم التسلسلي:",
      "repport_details": "تفاصيل التقرير",
      "repport_building": "جارٍ إنشاء التقرير…",
      "repport_submitted": "تم الإرسال",
      "repport_download_pdf": "تنزيل PDF",
      "repport_pdf_generating": "جارٍ إنشاء ملف PDF...",
      "repport_answer": "الإجابة",
      "repport_unanswered": "بدون إجابة",
      "repport_no_questions_in_submission": "لا توجد أسئلة في هذا الإرسال.",
      "repport_pdf_title": "تقرير",
      "repport_pdf_date": "التاريخ",
      "repport_pdf_qr_id": "معرّف QR",
      "repport_pdf_serial": "الرقم التسلسلي",
      "repport_pdf_username": "اسم المستخدم",
      "repport_pdf_technician": "الفني",
      "repport_th_question": "السؤال",
      "repport_th_answer": "الإجابة",
      "repport_type": "النوع",
      "repport_type_open": "مفتوح",
      "repport_type_qcm": "اختيار متعدد",
      "repport_type_yesno": "نعم / لا",
      "repport_popup_blocked": "تم حظر النافذة المنبثقة. يرجى السماح بالنوافذ المنبثقة لتنزيل PDF.",
      "repport_confirm_delete_title": "هل تريد حذف العنوان \"{{title}}\" (مع كل العناوين الفرعية والأسئلة)؟",
      "repport_confirm_delete_subtitle": "هل تريد حذف العنوان الفرعي \"{{subtitle}}\" وكل أسئلته؟",
      "repport_confirm_delete_question": "هل تريد حذف هذا السؤال؟",
      "repport_err_app_missing": "التطبيق غير محدد (localStorage).",
      "repport_err_title_required": "العنوان مطلوب.",
      "repport_err_title_exists": "هذا العنوان موجود بالفعل لهذا التطبيق.",
      "repport_err_title_add_failed": "فشل إضافة العنوان.",
      "repport_err_title_delete_failed": "فشل حذف العنوان.",
      "repport_err_select_title_first": "اختر عنواناً أولاً.",
      "repport_err_subtitle_required": "العنوان الفرعي مطلوب.",
      "repport_err_subtitle_exists": "هذا العنوان الفرعي موجود بالفعل.",
      "repport_err_subtitle_add_failed": "فشل إضافة العنوان الفرعي.",
      "repport_err_subtitle_delete_failed": "فشل حذف العنوان الفرعي.",
      "repport_err_select_title_subtitle_first": "اختر عنواناً وعنواناً فرعياً أولاً.",
      "repport_err_question_text_required": "نص السؤال مطلوب.",
      "repport_err_invalid_question_type": "نوع السؤال غير صالح.",
      "repport_err_qcm_options_required": "لأسئلة الاختيار المتعدد، أضف خياراً واحداً على الأقل.",
      "repport_question_added": "تمت إضافة السؤال.",
      "repport_err_question_add_failed": "فشل إضافة السؤال.",
      "repport_err_question_delete_failed": "فشل حذف السؤال.",
      "repport_title_added": "تمت إضافة العنوان.",
      "repport_subtitle_added": "تمت إضافة العنوان الفرعي.",
      "repport_err_titles_load": "فشل تحميل العناوين.",
      "repport_err_subtitles_load": "فشل تحميل العناوين الفرعية.",
      "repport_err_questions_load": "فشل تحميل الأسئلة.",
      "repport_err_history_load": "فشل تحميل السجل.",
      "repport_err_build_report": "فشل إنشاء التقرير (meta/questions).",
      "repport_err_pdf_download": "يتعذر تنزيل ملف PDF حالياً.",
      "repport_history_app_required": "التطبيق مطلوب.",
      "repport_history_success": "تم تحميل السجل بنجاح.",
      "repport_history_db_error": "خطأ في قاعدة البيانات أثناء تحميل السجل.",
      "repport_meta_required_ids": "معرّفات الأسئلة مطلوبة.",
      "repport_meta_no_valid_ids": "لم يتم تقديم معرّفات صالحة.",
      "repport_meta_success": "تم تحميل البيانات الوصفية بنجاح.",
      "repport_meta_db_error": "خطأ في قاعدة البيانات أثناء تحميل البيانات الوصفية.",
      "ur_app_required": "معامل التطبيق (application) مطلوب.",
      "ur_users_loaded": "تم تحميل المستخدمين.",
      "ur_server_error": "خطأ في الخادم. حاول مرة أخرى لاحقاً.",
      "ur_missing_fields": "حقول مطلوبة مفقودة.",
      "ur_tech_fields_required": "التخصص والقطاع مطلوبان للفنيين.",
      "ur_email_exists": "هذا البريد الإلكتروني مسجل بالفعل.",
      "ur_username_exists": "اسم المستخدم مستخدم بالفعل لهذا التطبيق.",
      "ur_register_success": "تم تسجيل المستخدم بنجاح.",
      "ur_id_required": "حقل مطلوب مفقود: id.",
      "ur_user_not_found": "المستخدم غير موجود.",
      "ur_delete_success": "تم حذف المستخدم بنجاح.",
      "na": "غير متوفر",
      "unknown": "غير معروف",
      "client": "العميل",
      "technician": "الفني",
      "type": "النوع",
      "required": "إلزامي",
      "view": "عرض",
      "rp_qr": "رمز الاستجابة السريعة",
      "no_data": "لا توجد بيانات",
      "signature": "التوقيع",
      "add": "إضافة",
      "admin_manage_apps_users": "إدارة التطبيقات والمستخدمين",
      "admin_users_total": "إجمالي المستخدمين",
      "application": "التطبيق",
      "auth_login_story_text": "سجّل الدخول للوصول إلى رموز QR والتقارير والتدخلات.",
      "auth_login_story_title": "مرحباً بعودتك",
      "auth_proof_access": "وصول آمن",
      "auth_proof_qr": "أجهزة متصلة",
      "auth_proof_reports": "تقارير تلقائية",
      "auth_signup_feature_1": "إدارة رموز QR",
      "auth_signup_feature_2": "تقارير ذكية",
      "auth_signup_feature_3": "جدولة التدخلات",
      "auth_signup_story_text": "أنشئ مساحتك وابدأ رقمنة الصيانة.",
      "auth_signup_story_title": "ابدأ مع AssistByScan",
      "darkMode": {
        "toggle": "Toggle dark mode"
      },
      "date": "التاريخ",
      "details": "التفاصيل",
      "filter": "تصفية",
      "forgot_intro": "أدخل بريدك الإلكتروني لتلقي رابط إعادة التعيين.",
      "forgot_resend_window": "يمكنك إعادة الإرسال خلال {{seconds}} ثانية.",
      "forgot_secure_link": "تم إرسال رابط آمن إلى البريد الإلكتروني.",
      "forgot_story_title": "إعادة تعيين كلمة المرور",
      "help_replace_video": "استبدال الفيديو",
      "language": "اللغة",
      "qr_generated_title": "رموز QR المُولدة",
      "reset_success": "تمت إعادة تعيين كلمة المرور بنجاح.",
      "rp_popup_blocked": "تم حظر النافذة المنبثقة. يرجى السماح بالنوافذ المنبثقة.",
      "static_title": "الصفحات الثابتة",
      "theme_dark": "الوضع الداكن",
      "theme_light": "الوضع الفاتح",
      "urw_add_other": "إضافة مستخدم آخر",
      "urw_subtitle": "سجّل مستخدمين لتطبيقك.",
      "urw_success_title": "تم تسجيل المستخدم بنجاح"
    }
  },
  "fa": {
    "translation": {
      "nav": {
        "features": "ویژگی‌ها",
        "howItWorks": "نحوه کار",
        "pricing": "قیمت‌گذاری",
        "contact": "تماس با ما",
        "login": "ورود",
        "home": "خانه",
        "register": "ثبت‌نام"
      },
      "hero": {
        "badge": "پلتفرم SaaS · نگهداری دیجیتال با کد QR",
        "title": "دیجیتالی کردن مداخلات فنی خود با یک اسکن ساده",
        "subtitle": "هر دستگاه به یک کد QR متصل است، هر مداخله ردیابی می‌شود، هر گزارش به طور خودکار تولید می‌شود. یک پلتفرم وب + موبایل برای متمرکز و خودکارسازی تمام تعمیر و نگهداری شما.",
        "login": "ورود",
        "signup": "ثبت نام",
        "discover": "مشاهده پلتفرم"
      },
      "pricing": {
        "label": "قیمت‌ها",
        "title": "رایگان امتحان کنید، فقط در صورت رضایت پرداخت کنید",
        "subtitle": "با نسخه رایگان شروع کنید، سپس هر زمان آماده بودید Pro را با پرداخت کارت آزمایشی فعال کنید.",
        "noCreditCard": "پرداخت کارت آزمایشی",
        "noCommitment": "بدون تعهد",
        "cancelAnytime": "لغو در هر زمان",
        "payment": {
          "demoBadge": "پرداخت کارت آزمایشی",
          "title": "اشتراک Pro را فعال کنید",
          "subtitle": "فعال‌سازی را با کارت تست کامل کنید. در این حالت هیچ پرداخت واقعی انجام نمی‌شود.",
          "cardName": "نام روی کارت",
          "cardNamePlaceholder": "علی رضایی",
          "cardNumber": "شماره کارت",
          "expiry": "انقضا",
          "cvc": "CVC",
          "demoNotice": "حالت پرداخت غیرفعال است: این تأیید پرداخت را شبیه‌سازی کرده و اشتراک برنامه شما را فعال می‌کند.",
          "mockOnly": "تراکنش شبیه‌سازی‌شده",
          "noCharge": "بدون برداشت بانکی",
          "activateNow": "فعال‌سازی فوری",
          "missingApp": "قبل از فعال‌سازی اشتراک، با برنامه خود وارد شوید.",
          "invalid": "لطفاً اطلاعات کارت تست را کامل کنید.",
          "processing": "در حال فعال‌سازی…",
          "payButton": "فعال‌سازی اشتراک",
          "success": "اشتراک فعال شد. اکنون می‌توانید QR بیشتری تولید کنید.",
          "backToQr": "بازگشت به تولیدکننده QR"
        },
        "plans": [
          {
            "name": "آزمایش رایگان",
            "price": "€0",
            "period": "برای ۱۴ روز",
            "description": "تمام امکانات را بدون تعهد و بدون کارت بانکی امتحان کنید.",
            "badge": "۱۴ روز رایگان",
            "cta": "شروع آزمایش",
            "features": [
              "دسترسی کامل به پلتفرم",
              "۵ کد QR شامل شده",
              "تا ۵ کاربر",
              "فرم‌های پویا",
              "گزارش PDF",
              "پشتیبانی ایمیلی"
            ]
          },
          {
            "name": "Pro",
            "price": "€149",
            "period": "/ماه",
            "description": "پس از پایان دوره آزمایشی، برای ادامه بدون وقفه به Pro ارتقا دهید.",
            "badge": "",
            "cta": "انتخاب پلن Pro",
            "features": [
              "همه امکانات نسخه رایگان",
              "کاربران نامحدود",
              "زمان‌بندی پیشرفته",
              "اعلان‌های لحظه‌ای",
              "دسترسی API",
              "پشتیبانی ویژه",
              "خروجی PDF پیشرفته"
            ]
          },
          {
            "name": "Enterprise",
            "price": "سفارشی",
            "period": "",
            "description": "برای سازمان‌های بزرگ با نیازهای خاص و چندسایتی.",
            "badge": "",
            "cta": "تماس با ما",
            "features": [
              "همه امکانات Pro",
              "SSO و SAML",
              "SLA تضمین‌شده",
              "مدیر حساب اختصاصی",
              "استقرار داخلی",
              "آموزش حضوری"
            ]
          }
        ]
      },
      "features": {
        "badge": "ویژگی‌ها",
        "title": "هر آنچه برای دیجیتالی کردن نگهداری خود نیاز دارید",
        "subtitle": "یک پلتفرم کامل که هر مرحله را پوشش می‌دهد، از اسکن کد QR تا خروجی گزارش نهایی.",
        "qrcodes": {
          "title": "کدهای QR هوشمند",
          "desc": "هر دستگاه به یک کد QR منحصر به فرد متصل است. یک اسکن ساده فرم مناسب برای تجهیزات و زمینه مداخله را باز می‌کند."
        },
        "forms": {
          "title": "فرم‌های پویا",
          "desc": "ایجاد پرسشنامه‌های سلسله‌مراتبی (عنوان‌ها، زیرعنوان‌ها، سوالات) با پاسخ‌های آزاد، انتخاب چندگانه یا بله/خیر، مناسب برای هر نوع مداخله."
        },
        "planning": {
          "title": "برنامه‌ریزی هوشمند",
          "desc": "سیستم به طور خودکار زمان‌های موجود را بر اساس برنامه‌های هر تکنسین، روزهای کاری و تعطیلات پیشنهاد می‌دهد."
        },
        "reports": {
          "title": "گزارش‌های خودکار",
          "desc": "پس از اتمام مداخله، یک گزارش ساختاریافته با تمام داده‌های جمع‌آوری شده تولید می‌شود که قابل مشاهده و خروجی به PDF است."
        },
        "notifications": {
          "title": "اعلان‌های بی‌درنگ",
          "desc": "مدیران، تکنسین‌ها و مشتریان در هر مرحله مطلع می‌شوند: درخواست جدید، انتساب، مداخله تکمیل شده."
        },
        "roles": {
          "title": "مدیریت چند نقشی",
          "desc": "مدیریت تیم خود با نقش‌های متمایز (مدیر، تکنسین، مشتری) و مجوزهای مناسب برای هر پروفایل."
        },
        "mobile": {
          "title": "برنامه میدانی موبایل",
          "desc": "تکنسین‌ها و کاربران از برنامه Android کار می‌کنند: اسکن، تشخیص، ورود و ارسال مستقیم در میدان."
        },
        "languages": {
          "title": "پشتیبانی چند زبانه",
          "desc": "رابط و پیام‌ها به طور پویا با زبان هر کاربر از طریق سیستم ترجمه سمت سرور سازگار می‌شوند."
        },
        "history": {
          "title": "تاریخچه و فیلتر پیشرفته",
          "desc": "یافتن هر مداخله گذشته با استفاده از فیلترهای کاربر، دستگاه، تکنسین، تاریخ و وضعیت."
        }
      },
      "steps": {
        "badge": "نحوه کار",
        "title": "از اسکن تا گزارش در 5 مرحله",
        "subtitle": "فرآیندی روان و خودکار، از راه‌اندازی اولیه تا تولید گزارش نهایی.",
        "configure": {
          "title": "پیکربندی ماشین‌ها و فرم‌های خود",
          "desc": "از رابط وب، مدیر انواع ماشین‌ها را تعریف می‌کند و فرم‌های سلسله‌مراتبی با سوالات مناسب برای هر زمینه مداخله ایجاد می‌کند."
        },
        "qrcode": {
          "title": "تولید و استقرار کدهای QR خود",
          "desc": "هر تجهیزات یک کد QR منحصر به فرد دریافت می‌کند. آنها را چاپ کرده و روی ماشین‌های خود قرار دهید تا فوراً در دسترس قرار گیرند."
        },
        "scan": {
          "title": "اسکن، تشخیص و ارسال",
          "desc": "کاربر کد QR را از برنامه موبایل اسکن می‌کند، فرم مناسب (تشخیص، درخواست مستقیم) را پر می‌کند و درخواست خود را در چند ثانیه ارسال می‌کند."
        },
        "planning": {
          "title": "برنامه‌ریزی و انتساب خودکار",
          "desc": "سیستم تکنسین‌های در دسترس را بر اساس برنامه‌ها، مهارت‌ها و محدودیت‌های آنها شناسایی می‌کند، سپس به طور خودکار یک زمان مداخله پیشنهاد می‌دهد."
        },
        "report": {
          "title": "مداخله و گزارش تولید شده",
          "desc": "تکنسین مداخله می‌کند، داده‌های میدانی را تکمیل می‌کند. یک گزارش ساختاریافته به طور خودکار تولید می‌شود، قابل مشاهده آنلاین و خروجی به PDF."
        }
      },
      "showcase": {
        "badge": "محصول",
        "title": "وب + موبایل، یک پلتفرم واحد",
        "subtitle": "مدیر از داشبورد وب پیکربندی و نظارت می‌کند. تکنسین‌ها و کاربران از برنامه موبایل Android اسکن، تشخیص و مداخله می‌کنند."
      },
      "benefits": {
        "badge": "مزایا",
        "title": "نتایج ملموس از ماه اول",
        "time": {
          "title": "صرفه‌جویی در زمان",
          "desc": "نیازی به جستجو در تاریخچه یا پر کردن فرم‌های کاغذی نیست. هر مداخله به طور خودکار از اسکن تا گزارش ردیابی می‌شود.",
          "stat": "زمان پردازش"
        },
        "planning": {
          "title": "مداخلات بهتر برنامه‌ریزی شده",
          "desc": "موتور برنامه‌ریزی هوشمند در دسترس بودن واقعی، تعطیلات و مهارت‌های هر تکنسین را در نظر می‌گیرد.",
          "stat": "مداخلات بیشتر/روز"
        },
        "paperless": {
          "title": "صفر کاغذ، صفر اتلاف",
          "desc": "فرم‌های پویا جایگزین دفترچه‌ها می‌شوند. تمام داده‌ها ساختاریافته، ذخیره شده و در هر زمان از هر دستگاهی قابل دسترسی هستند.",
          "stat": "سند کاغذی"
        },
        "data": {
          "title": "داده‌های متمرکز",
          "desc": "تاریخچه کامل مداخلات، گزارش‌های قابل خروجی به PDF، فیلتر پیشرفته بر اساس تکنسین، دستگاه، تاریخ یا وضعیت - همه در یک مکان.",
          "stat": "قابلیت ردیابی"
        }
      },
      "cases": {
        "badge": "موارد استفاده",
        "title": "مناسب با بخش شما",
        "subtitle": "هر حوزه‌ای که داشته باشید، AssistByScan با فرآیندهای نگهداری شما سازگار می‌شود.",
        "industry": {
          "title": "صنعت",
          "desc": "نگهداری پیشگیرانه و اصلاحی در خطوط تولید. هر دستگاه با کد QR شناسایی می‌شود، هر مداخله ردیابی می‌شود."
        },
        "services": {
          "title": "خدمات نگهداری",
          "desc": "مدیریت چند مشتری و چند سایت. برنامه‌ریزی، انتساب و پیگیری مداخلات تکنسین‌های خود در زمان واقعی."
        },
        "retail": {
          "title": "خرده فروشی و تجارت",
          "desc": "ردیابی تجهیزات در فروشگاه‌ها (تهویه مطبوع، صندوق‌ها، روشنایی) با درخواست‌های مداخله یک اسکن."
        },
        "facility": {
          "title": "مدیریت تأسیسات",
          "desc": "مدیریت فنی ساختمان‌ها و فضاهای مشترک با قابلیت ردیابی کامل و گزارش‌های خودکار."
        }
      },
      "footer": {
        "contact": "تماس با ما",
        "privacy": "سیاست حفظ حریم خصوصی",
        "description": "پلتفرم نگهداری دیجیتال با کد QR.",
        "rights": "تمام حقوق محفوظ است."
      },
      "qr_app_not_found": "برنامه پیدا نشد.",
      "qr_limit_reached": "این برنامه به محدودیت ۵ کد QR در طرح رایگان رسیده است.",
      "qr_limit_exceeded": "این درخواست از تعداد کدهای QR باقی‌مانده در طرح رایگان بیشتر است.",
      "qr_limit_dialog_eyebrow": "طرح رایگان",
      "qr_limit_dialog_title": "محدودیت طرح رایگان تمام شد",
      "qr_limit_dialog_usage_label": "مصرف کدهای QR",
      "qr_limit_dialog_usage": "{{used}}/{{limit}} کد QR استفاده شده است. باقی‌مانده: {{remaining}}.",
      "qr_limit_dialog_benefit_qr": "کدهای QR نامحدود برای تجهیزات شما",
      "qr_limit_dialog_benefit_team": "مدیریت تیم و دسترسی وب/موبایل",
      "qr_limit_dialog_benefit_reports": "گزارش‌های متمرکز مداخله",
      "qr_limit_dialog_subscribe": "اشتراک",
      "subscription_app_missing": "برنامه برای فعال‌سازی اشتراک مشخص نیست.",
      "subscription_plan_invalid": "طرح اشتراک نامعتبر است.",
      "subscription_mock_success": "اشتراک فعال شد.",
      "subscription_mock_error": "فعال‌سازی اشتراک ناموفق بود.",
      "download_app": "دانلود اپلیکیشن",
      "assist_by_scan": "ASSIST BY SCAN",
      "login_title": "ورود",
      "email": "ایمیل",
      "password": "رمز عبور",
      "login": "ورود",
      "forgot_password": "فراموشی رمز عبور؟",
      "no_account": "حساب ندارید؟",
      "signup": "ثبت نام",
      "nav_login": "ورود",
      "nav_signup": "ثبت نام",
      "msg_enter_email_password": "لطفاً ایمیل و رمز عبور را وارد کنید.",
      "msg_invalid_email": "لطفاً یک ایمیل معتبر وارد کنید.",
      "msg_unknown_error": "خطای ناشناخته.",
      "msg_server_or_network": "خطای سرور یا شبکه.",
      "msg_incorrect_email_or_password": "ایمیل یا رمز عبور نادرست است.",
      "msg_login_success": "ورود با موفقیت انجام شد!",
      "admin_dashboard": "داشبورد ادمین",
      "admin_loading_users": "در حال بارگذاری کاربران...",
      "admin_error_fetch_users": "خطا در دریافت کاربران.",
      "admin_error_delete_user": "خطا در حذف کاربر.",
      "admin_confirm_delete": "آیا مطمئن هستید که می‌خواهید این کاربر را حذف کنید؟",
      "admin_th_id": "شناسه",
      "admin_th_application": "اپلیکیشن",
      "admin_th_type": "نوع",
      "admin_th_email": "ایمیل",
      "admin_th_city": "شهر",
      "admin_th_country": "کشور",
      "admin_th_status": "وضعیت",
      "admin_th_role": "نقش",
      "admin_th_qr_count": "تعداد QR",
      "admin_th_actions": "عملیات",
      "status_active": "فعال",
      "status_not_active": "غیرفعال",
      "delete": "حذف",
      "logout": "خروج",
      "qr_generator": "تولید QR",
      "questions": "سوالات",
      "report": "گزارش",
      "static_page": "صفحه ثابت",
      "help_center": "مرکز راهنما",
      "register_admin": "ثبت ادمین",
      "register_user": "ثبت کاربر",
      "contact_us": "تماس با ما",
      "contact_title": "تماس با ما",
      "contact_direct_title": "یا مستقیماً با ما تماس بگیرید",
      "contact_phone_label": "تلفن",
      "contact_subtitle": "نیاز به کمک دارید؟ برای ما پیام بفرستید، در اسرع وقت پاسخ می‌دهیم.",
      "contact_name": "نام شما",
      "contact_email": "ایمیل شما",
      "contact_subject": "موضوع",
      "contact_message_placeholder": "پیام خود را بنویسید...",
      "contact_send": "ارسال پیام",
      "contact_sending": "در حال ارسال...",
      "contact_reset": "بازنشانی",
      "contact_all_required": "تمام فیلدها الزامی هستند.",
      "contact_message_short": "پیام خیلی کوتاه است (حداقل 10 کاراکتر).",
      "contact_success": "پیام با موفقیت ارسال شد.",
      "contact_failed": "ارسال پیام ناموفق بود.",
      "contact_error_general": "خطا در ارسال پیام. لطفاً دوباره تلاش کنید.",
      "contact_email_label": "ایمیل",
      "contact_name_required": "نام الزامی است.",
      "contact_subject_required": "موضوع الزامی است.",
      "contact_message_required": "پیام الزامی است.",
      "reset_title": "ایجاد رمز عبور جدید",
      "reset_verifying": "در حال بررسی لینک…",
      "reset_new_password": "رمز عبور جدید",
      "reset_confirm_password": "تأیید رمز عبور",
      "reset_saving": "در حال ذخیره…",
      "save": "ذخیره",
      "show": "نمایش",
      "hide": "مخفی",
      "reset_remember": "یادت آمد؟",
      "reset_fill_both_fields": "لطفاً هر دو فیلد را پر کنید.",
      "reset_pwd_rules": "رمز عبور ضعیف است: حداقل ۸ کاراکتر، حروف بزرگ، عدد و کاراکتر ویژه لازم است.",
      "reset_missing_link": "لینک بازیابی موجود نیست.",
      "reset_missing_link_long": "لینک بازیابی موجود نیست. لطفاً از لینکی که ایمیل شده استفاده کنید.",
      "reset_success_redirect": "رمز عبور به‌روزرسانی شد. در حال انتقال…",
      "reset_err_invalid_or_expired": "لینک نامعتبر یا منقضی شده است.",
      "reset_err_unexpected": "خطای غیرمنتظره.",
      "reset_err_server": "خطای سرور.",
      "reset_err_weak_password": "رمز عبور ضعیف است.",
      "reset_err_password_mismatch": "رمزهای عبور یکسان نیستند.",
      "reset_err_expired": "لینک منقضی شده است. یک لینک جدید درخواست کنید.",
      "reset_err_missing_fields": "لطفاً همه فیلدها را پر کنید.",
      "reset_err_invalid_or_used": "لینک نامعتبر است یا قبلاً استفاده شده است.",
      "reset_processed": "اگر حساب وجود داشته باشد، بازنشانی رمز عبور پردازش شد.",
      "download_title": "نصب Assist by Scan",
      "download_mobile_text": "برای دانلود برنامه روی دکمه زیر بزنید:",
      "download_desktop_text": "برای دانلود برنامه، این کد QR را با دستگاه اندروید اسکن کنید:",
      "download_apk": "دانلود برنامه (APK)",
      "download_feature_android": "اندروید میدانی",
      "download_feature_scan": "اسکن فوری QR",
      "download_feature_secure": "دسترسی امن",
      "download_holo_scan_fast": "اسکن سریع",
      "download_holo_apk_secure": "APK امن",
      "download_tap": "لمس",
      "download_scan": "اسکن",
      "editable_placeholder": "متن {{title}} را اینجا ویرایش کنید...",
      "editable_load_failed": "بارگذاری متن «{{title}}» ناموفق بود.",
      "editable_text_empty": "متن نمی‌تواند خالی باشد.",
      "editable_update_success": "متن «{{title}}» با موفقیت به‌روزرسانی شد.",
      "editable_update_failed": "به‌روزرسانی ناموفق بود.",
      "edit": "ویرایش",
      "saving": "در حال ذخیره…",
      "cancel": "لغو",
      "forgot_title": "فراموشی رمز عبور",
      "forgot_email_required": "ایمیل الزامی است.",
      "forgot_email_sent": "در صورت وجود حساب، لینک بازنشانی ارسال شده است.",
      "forgot_send_link": "ارسال لینک بازنشانی",
      "forgot_sending": "در حال ارسال…",
      "forgot_resend_in": "ارسال مجدد در {{seconds}} ثانیه",
      "forgot_remember": "رمز عبور را به یاد دارید؟",
      "help_title": "مدیریت وظایف راهنما",
      "loading": "در حال بارگذاری...",
      "help_load_failed": "بارگذاری وظایف ناموفق بود.",
      "help_add_new": "افزودن وظیفه جدید",
      "help_new_title_ph": "عنوان وظیفه جدید",
      "help_new_content_ph": "محتوای وظیفه جدید",
      "help_add_btn": "افزودن",
      "help_add_required": "عنوان و محتوا الزامی است.",
      "help_app_missing": "اپلیکیشن موجود نیست (localStorage userApplication).",
      "help_add_success": "وظیفه جدید اضافه شد.",
      "help_add_failed": "افزودن ناموفق بود:",
      "help_item_empty": "عنوان و محتوا نمی‌توانند خالی باشند.",
      "help_item_saved": "وظیفه با موفقیت ذخیره شد.",
      "help_item_save_failed": "ذخیره ناموفق بود:",
      "help_confirm_delete_task": "آیا مطمئن هستید که می‌خواهید این وظیفه را حذف کنید؟",
      "help_deleted": "حذف شد.",
      "help_delete_failed": "حذف ناموفق بود.",
      "help_toggle_title": "برای نمایش/مخفی کردن توضیحات کلیک کنید",
      "help_upload_video": "آپلود ویدئو",
      "help_change_video": "تغییر ویدئو",
      "help_video_badge": "ویدئو",
      "help_delete_video": "حذف ویدئو",
      "help_default_1_title": "چطور کد QR را اسکن کنیم؟",
      "help_default_1_content": "اپ موبایل را باز کنید، روی دکمه اسکن بزنید و دوربین را روی کد QR دستگاه بگیرید.",
      "help_default_2_title": "چطور گزارش بسازیم؟",
      "help_default_2_content": "بعد از تکمیل مداخله، روی تولید گزارش بزنید تا یک PDF خودکار با همه داده‌ها ساخته شود.",
      "help_default_3_title": "چطور کاربر اضافه کنیم؟",
      "help_default_3_content": "به صفحه ثبت کاربر بروید و فرم را با ایمیل، نام کاربری و نقش تکمیل کنید.",
      "help_choose_video": "لطفاً یک فایل ویدئویی انتخاب کنید.",
      "help_video_uploaded": "ویدئو آپلود شد.",
      "help_upload_failed": "آپلود ناموفق بود:",
      "help_tasks_required_params": "پارامتر application وجود ندارد.",
      "help_tasks_not_found": "هیچ وظیفه راهنمایی پیدا نشد.",
      "help_tasks_success": "وظایف بارگذاری شد.",
      "help_tasks_server_error": "خطای سرور هنگام بارگذاری وظایف.",
      "help_task_not_found": "وظیفه راهنما یافت نشد.",
      "help_video_missing_field": "فیلد 'video' وجود ندارد (multipart/form-data).",
      "help_no_file_selected": "فایلی انتخاب نشده است.",
      "help_invalid_video_format": "فرمت ویدئو نامعتبر است. فرمت‌های مجاز: mp4, webm, ogg, mov, m4v.",
      "pwd_success_title": "رمز عبور تغییر کرد",
      "pwd_success_main": "رمز عبور شما با موفقیت تغییر کرد.",
      "pwd_success_hint": "می‌توانید این صفحه را ببندید و برنامه را دوباره باز کنید تا وارد شوید.",
      "qr_title": "تولیدکننده QR",
      "qr_views_aria": "نمایش‌های QR",
      "qr_tab_generate": "تولید QR",
      "qr_tab_history": "مشاهده تاریخچه",
      "qr_count_aria": "تعداد QR برای تولید",
      "qr_size_aria": "اندازه QR بر حسب سانتی‌متر",
      "qr_size_option": "{{size}} سانتی‌متر",
      "generate": "تولید",
      "qr_generating": "در حال تولید…",
      "qr_generation_progress": "تولید در حال انجام… ⏳",
      "qr_download_a4_pdf": "دانلود PDF A4",
      "qr_download_pdf": "دانلود PDF",
      "prev": "→ قبلی",
      "next": "بعدی ←",
      "page": "صفحه",
      "of": "از",
      "qr_loading_history": "در حال بارگذاری تاریخچه… ⏳",
      "qr_no_history": "هیچ QR در تاریخچه وجود ندارد.",
      "qr_status_active": "فعال",
      "qr_status_not_active": "غیرفعال",
      "print": "چاپ",
      "qr_print_title": "چاپ QR",
      "qr_alt": "QR برای {{code}}",
      "qr_confirm_delete": "آیا واقعاً می‌خواهید این QR را حذف کنید؟",
      "qr_err_invalid_number": "لطفاً یک عدد معتبر وارد کنید (≥ 1).",
      "qr_err_generate": "خطا در تولید QR. لطفاً دوباره تلاش کنید.",
      "qr_err_delete": "خطا در حذف QR.",
      "qr_err_history": "خطا در دریافت تاریخچه QR. لطفاً دوباره تلاش کنید.",
      "qr_err_pdf_single": "دانلود PDF ناموفق بود. CORS / دسترسی تصویر را بررسی کنید.",
      "qr_err_pdf_export": "خروجی PDF ناموفق بود. CORS / دسترسی تصویر را بررسی کنید.",
      "qr_app_missing": "اپلیکیشن مشخص نیست.",
      "qr_generate_success": "کدهای QR ایجاد شدند.",
      "qr_history_success": "تاریخچه بارگذاری شد.",
      "qr_not_found": "کد QR پیدا نشد.",
      "qr_delete_active_forbidden": "حذف کد QR فعال امکان‌پذیر نیست.",
      "qr_delete_success": "کد QR با موفقیت حذف شد.",
      "qf_type_required": "نوع ماشین الزامی است.",
      "qf_type_added": "نوع ماشین اضافه شد.",
      "qf_type_cannot_empty": " نوع نمی‌تواند خالی باشد.",
      "qf_type_updated": "نوع ماشین به‌روزرسانی شد.",
      "qf_confirm_delete_type": "این نوع ماشین (و تمام سوالاتش) حذف شود؟",
      "qf_type_deleted": "نوع ماشین حذف شد.",
      "qf_app_missing": "اپلیکیشن موجود نیست (localStorage userApplication).",
      "qf_select_machine_type_placeholder": "— انتخاب نوع ماشین —",
      "qf_type_open": "باز",
      "qf_type_qcm": "چندگزینه‌ای",
      "qf_type_yesno": "بله / خیر",
      "qf_qcm_options_ph": "گزینه‌ها با ویرگول: مثل \"کم, متوسط, زیاد\"",
      "qf_question_ph": "سوال خود را اینجا بنویسید…",
      "qf_it_report_repair": "گزارش تعمیر",
      "qf_it_ask_repair": "درخواست عملیات",
      "qf_filter_ask_repair": "درخواست تعمیر",
      "qf_question_added": "سوال با موفقیت اضافه شد.",
      "qf_questions_list_title": "لیست سوالات",
      "qf_filters_aria": "فیلترهای سؤال",
      "qf_delete_selected": "حذف انتخاب‌شده‌ها",
      "qf_question_deleted": "سوال حذف شد.",
      "qf_question_deleted_with_id": "سوال {{id}} حذف شد.",
      "qf_question_updated": "سوال به‌روزرسانی شد.",
      "qf_confirm_delete_question": "این سوال حذف شود؟",
      "qf_options": "گزینه‌ها",
      "qf_type_badge": "نوع",
      "qf_machine_types_title": "انواع دستگاه‌ها",
      "qf_add_type_ph": "افزودن نوع دستگاه…",
      "qf_add_type_btn": "افزودن",
      "qf_add_question_title": "افزودن سوال",
      "qf_filter_all": "فیلتر: همه",
      "qf_col_question": "سوال",
      "qf_col_type": "نوع",
      "qf_col_machine": "دستگاه",
      "qf_col_intervention": "مداخله",
      "qf_options_placeholder": "گزینه ۱، گزینه ۲، گزینه ۳",
      "qf_delete_selected_success": "{{count}} سؤال حذف شد.",
      "qf_select_intervention": "لطفاً نوع عملیات را انتخاب کنید.",
      "qf_select_machine_type": "لطفاً نوع ماشین را انتخاب کنید.",
      "qf_text_required": "لطفاً سؤال را وارد کنید.",
      "qf_text_cannot_empty": "متن نمی‌تواند خالی باشد.",
      "qf_invalid_question_type": "نوع سؤال نامعتبر است.",
      "qf_qcm_need_options": "برای سؤال چندگزینه‌ای باید گزینه وارد کنید.",
      "qf_select_at_least_one": "حداقل یک سؤال را انتخاب کنید.",
      "qf_err_load_types": "بارگذاری انواع دستگاه‌ها ناموفق بود.",
      "qf_err_load_questions": "بارگذاری سؤالات ناموفق بود.",
      "qf_type_add_failed": "افزودن نوع دستگاه ناموفق بود.",
      "qf_type_update_failed": "به‌روزرسانی نوع دستگاه ناموفق بود.",
      "qf_type_delete_failed": "حذف نوع دستگاه ناموفق بود.",
      "qf_type_delete_conflict": "حذف ممکن نیست: این نوع در حال استفاده است.",
      "qf_question_add_failed": "افزودن سؤال ناموفق بود.",
      "qf_question_update_failed": "به‌روزرسانی سؤال ناموفق بود.",
      "qf_question_delete_failed": "حذف سؤال ناموفق بود.",
      "qf_delete_selected_failed": "حذف انتخاب‌شده‌ها ناموفق بود.",
      "qf_select_question_aria": "انتخاب سؤال {{id}}",
      "close": "بستن",
      "choose_file": "انتخاب فایل",
      "no_file_selected": "فایلی انتخاب نشده",
      "uploading": "در حال آپلود...",
      "upload": "آپلود",
      "replace": "جایگزینی",
      "help_video_choose_hint": "یک ویدئو از کامپیوتر انتخاب کنید.",
      "help_video_replace_hint": "برای جایگزینی، یک ویدئوی جدید انتخاب کنید.",
      "signup_title": "ایجاد حساب",
      "city": "شهر",
      "country": "کشور",
      "signup_confirm_password": "تأیید رمز عبور",
      "signup_loading": "در حال ثبت نام…",
      "signup_have_account": "حساب دارید؟",
      "show_password": "نمایش رمز عبور",
      "hide_password": "مخفی کردن رمز عبور",
      "signup_fix_errors": "لطفاً خطاهای مشخص‌شده را اصلاح کنید.",
      "signup_success_generic": "اگر ایمیل معتبر باشد، لینک تأیید ارسال شده است.",
      "signup_err_email_required": "ایمیل الزامی است.",
      "signup_err_city_required": "شهر الزامی است.",
      "signup_err_application_required": "اپلیکیشن الزامی است.",
      "signup_err_country_required": "کشور الزامی است.",
      "signup_err_password_required": "رمز عبور الزامی است.",
      "signup_err_password_weak": "حداقل ۸ کاراکتر، یک حرف بزرگ، یک عدد و یک کاراکتر خاص.",
      "signup_err_confirm_required": "لطفاً رمز عبور را تأیید کنید.",
      "signup_err_password_mismatch": "رمزهای عبور یکسان نیستند.",
      "signup_err_invalid_value": "مقدار نامعتبر.",
      "signup_err_validation": "خطاهای اعتبارسنجی.",
      "signup_err_generic": "خطا در ثبت نام. لطفاً بعداً دوباره تلاش کنید.",
      "signup_err_email_exists": "ایمیل قبلاً ثبت شده است.",
      "signup_err_application_exists": "اپ قبلاً ثبت شده است.",
      "static_about": "درباره ما",
      "static_terms": "شرایط استفاده",
      "static_privacy": "سیاست حفظ حریم خصوصی",
      "add_user": "افزودن کاربر",
      "username": "نام کاربری",
      "select_role": "انتخاب نقش",
      "role": "نقش",
      "role_technician": "تکنسین",
      "role_client": "مشتری",
      "specialty": "تخصص",
      "sector": "منطقه / بخش",
      "register": "ثبت",
      "registering": "در حال ثبت...",
      "users_list": "لیست کاربران",
      "status": "وضعیت",
      "actions": "عملیات",
      "active": "فعال",
      "not_active": "غیرفعال",
      "no_users": "کاربری یافت نشد.",
      "all_fields_required": "تمام فیلدها الزامی هستند.",
      "tech_fields_required": "تخصص و بخش برای تکنسین‌ها الزامی است.",
      "user_registered": "کاربر با موفقیت ثبت شد.",
      "registration_error": "خطا در ثبت.",
      "registration_failed": "ثبت نام ناموفق بود.",
      "confirm_delete_user": "آیا مطمئن هستید که می‌خواهید این کاربر را حذف کنید؟",
      "user_deleted": "کاربر با موفقیت حذف شد.",
      "delete_failed": "حذف کاربر ناموفق بود.",
      "delete_error": "خطا در حذف کاربر.",
      "user_load_failed": "بارگذاری کاربران ناموفق بود.",
      "user_fetch_error": "خطا در دریافت کاربران.",
      "urw_title": "مدیر جدید اضافه کنید",
      "urw_ph_email": "ایمیل *",
      "urw_ph_application": "اپلیکیشن *",
      "urw_role_legend": "انتخاب نقش:",
      "urw_role_user": "کاربر",
      "urw_role_admin": "ادمین",
      "urw_type_legend": "انتخاب نوع:",
      "urw_type_scan": "اسکن",
      "urw_type_direct": "مستقیم",
      "urw_type_both": "هر دو",
      "urw_register": "ثبت",
      "urw_registering": "در حال ثبت…",
      "urw_fill_required": "لطفاً همه فیلدهای ضروری را پر کنید.",
      "urw_success": "کاربر با موفقیت ثبت شد!",
      "urw_failed": "ثبت ناموفق بود.",
      "urw_db_error": "ایمیل قبلاً ثبت شده است یا خطای پایگاه داده.",
      "verify_verifying_title": "در حال تأیید…",
      "verify_verifying_text": "لطفاً کمی صبر کنید.",
      "verify_success_title": "موفقیت",
      "verify_success_register": "ایمیل شما با موفقیت تأیید شد.",
      "verify_success_change_email": "آدرس ایمیل شما با موفقیت تغییر یافت.",
      "verify_success_delete": "حساب شما حذف شد.",
      "verify_fail_title": "خطا در تأیید",
      "verify_missing_token": "توکن تأیید موجود نیست.",
      "verify_invalid": "لینک نامعتبر یا منقضی شده است.",
      "verify_failed": "تأیید ناموفق بود.",
      "verify_go_login": "رفتن به ورود",
      "verify_back_login": "بازگشت به ورود",
      "VERIFY_TOKEN_MISSING": "رمز التحقق مفقود.",
      "VERIFY_TOKEN_INVALID_OR_USED": "الرابط غير صالح أو تم استخدامه بالفعل.",
      "VERIFY_TOKEN_EXPIRED": "انتهت صلاحية الرابط.",
      "VERIFY_INVALID_PAYLOAD": "بيانات التحقق غير صالحة.",
      "verify_invalid_flow": "مسار التحقق غير صالح.",
      "verify_db_error": "خطأ في الخادم/قاعدة البيانات.",
      "verify_new_email_already_in_use": "عنوان البريد الإلكتروني الجديد مستخدم بالفعل.",
      "VERIFY_REGISTER_SUCCESS": "تم التحقق من بريدك الإلكتروني بنجاح.",
      "verify_change_email_success": "تم تغيير بريدك الإلكتروني بنجاح.",
      "verify_delete_account_success": "تم حذف حسابك بنجاح.",
      "msg_email_required": "ایمیل الزامی است.",
      "msg_no_data_received": "هیچ داده‌ای دریافت نشد.",
      "msg_password_required": "رمز عبور الزامی است.",
      "msg_database_error": "خطای پایگاه داده.",
      "msg_processing_error": "خطا در پردازش.",
      "admin_user_not_found": "کاربر یافت نشد.",
      "admin_user_deleted_success": "کاربر با موفقیت حذف شد.",
      "MACHINE_TYPES_REQUIRED_APP": "اپلیکیشن الزامی است.",
      "MACHINE_TYPES_SUCCESS": "انواع دستگاه‌ها بارگذاری شد.",
      "MACHINE_TYPES_TYPE_REQUIRED": "فیلد \"type\" الزامی است.",
      "MACHINE_TYPES_ADDED": "نوع دستگاه اضافه شد.",
      "MACHINE_TYPES_ALREADY_EXISTS": "این نوع برای این اپلیکیشن از قبل وجود دارد.",
      "MACHINE_TYPES_NOT_FOUND": "نوع دستگاه پیدا نشد.",
      "MACHINE_TYPES_UPDATED": "نوع دستگاه به‌روزرسانی شد.",
      "MACHINE_TYPES_DELETED": "نوع دستگاه حذف شد.",
      "MACHINE_TYPES_FORBIDDEN": "دسترسی غیرمجاز.",
      "MACHINE_TYPES_SERVER_ERROR": "خطای سرور.",
      "QUESTIONS_REQUIRED_APP": "اپلیکیشن الزامی است.",
      "QUESTIONS_SUCCESS": "سؤالات بارگذاری شد.",
      "QUESTIONS_TEXT_REQUIRED": "متن الزامی است.",
      "QUESTIONS_MACHINE_TYPE_REQUIRED": "نوع دستگاه الزامی است.",
      "QUESTIONS_INVALID_INTERVENTION": "نوع مداخله نامعتبر است.",
      "QUESTIONS_INVALID_TYPE": "نوع سؤال نامعتبر است.",
      "QUESTIONS_QCM_OPTIONS_REQUIRED": "برای سؤال چندگزینه‌ای گزینه‌ها الزامی هستند.",
      "QUESTIONS_ADDED": "سؤال اضافه شد.",
      "QUESTIONS_NOT_FOUND": "سؤال پیدا نشد.",
      "QUESTIONS_DELETED": "سؤال حذف شد (شناسه: {{id}}).",
      "QUESTIONS_UPDATED": "سؤال به‌روزرسانی شد.",
      "QUESTIONS_SERVER_ERROR": "خطای سرور.",
      "repport_title": "مدیریت گزارش‌ها",
      "repport_tabs_aria": "تب‌های گزارش",
      "repport_tab_manage": "مدیریت",
      "repport_tab_history": "تاریخچه",
      "repport_new_title_placeholder": "عنوان گزارش جدید...",
      "repport_new_subtitle_placeholder": "زیرعنوان جدید...",
      "repport_search_placeholder": "جستجو با نام کاربری، QR ID، شماره سریال یا تکنسین...",
      "repport_col_date": "تاریخ",
      "repport_col_user": "کاربر",
      "repport_col_technician": "تکنسین",
      "repport_col_qr": "QR ID",
      "repport_detail_title": "جزئیات گزارش",
      "repport_serial": "شماره سریال",
      "repport_answers": "پاسخ‌ها",
      "repport_field_question": "سوال",
      "repport_field_type": "نوع",
      "repport_add_title": "افزودن عنوان جدید",
      "repport_title_ph": "عنوان",
      "repport_add_title_btn": "افزودن عنوان",
      "repport_loading_titles": "در حال بارگذاری عنوان‌ها…",
      "repport_no_titles": "هنوز عنوانی وجود ندارد.",
      "repport_toggle_title": "برای نمایش/مخفی کردن زیرعنوان‌ها کلیک کنید",
      "repport_delete_title": "حذف این عنوان",
      "repport_loading_subtitles": "در حال بارگذاری زیرعنوان‌ها…",
      "repport_no_subtitles": "برای این عنوان زیرعنوانی وجود ندارد.",
      "repport_toggle_subtitle": "برای نمایش/مخفی کردن سوالات کلیک کنید",
      "repport_add_subtitle": "افزودن زیرعنوان",
      "repport_add_subtitle_for_title": "افزودن زیرعنوان برای این عنوان",
      "repport_subtitle_ph": "زیرعنوان جدید",
      "repport_add_subtitle_btn": "افزودن زیرعنوان",
      "repport_loading_questions": "در حال بارگذاری سوالات…",
      "repport_no_questions": "برای این زیرعنوان سوالی وجود ندارد.",
      "repport_add_question": "افزودن سوال",
      "repport_add_question_btn": "افزودن سوال",
      "repport_question_text_ph": "متن سوال",
      "repport_qcm_options": "گزینه‌ها (با ویرگول جدا کنید)",
      "repport_qcm_options_ph": "مثال: \"کم، متوسط، زیاد\"",
      "repport_loading_history": "در حال بارگذاری تاریخچه…",
      "repport_no_submissions": "هیچ موردی یافت نشد.",
      "repport_filter_label": "فیلتر بر اساس مشتری (نام کاربری)",
      "repport_filter_ph": "نام کاربری را تایپ کنید… (مثال: ehsan)",
      "repport_showing": "نمایش",
      "repport_results": "نتیجه",
      "repport_row_qr": "QR:",
      "repport_row_serial": "شماره سریال:",
      "repport_details": "جزئیات گزارش",
      "repport_building": "در حال ساخت گزارش…",
      "repport_submitted": "ثبت‌شده",
      "repport_download_pdf": "دانلود PDF",
      "repport_pdf_generating": "در حال ایجاد PDF...",
      "repport_answer": "پاسخ",
      "repport_unanswered": "بدون پاسخ",
      "repport_no_questions_in_submission": "در این ارسال هیچ سوالی وجود ندارد.",
      "repport_pdf_title": "گزارش",
      "repport_pdf_date": "تاریخ",
      "repport_pdf_qr_id": "شناسه QR",
      "repport_pdf_serial": "شماره سریال",
      "repport_pdf_username": "نام کاربری",
      "repport_pdf_technician": "تکنسین",
      "repport_th_question": "سوال",
      "repport_th_answer": "پاسخ",
      "repport_type": "نوع",
      "repport_type_open": "باز",
      "repport_type_qcm": "چندگزینه‌ای",
      "repport_type_yesno": "بله / خیر",
      "repport_popup_blocked": "پنجره بازشو مسدود شده است. برای دانلود PDF اجازه Pop-up بدهید.",
      "repport_confirm_delete_title": "عنوان \"{{title}}\" (به همراه همه زیرعنوان‌ها و سوالات) حذف شود؟",
      "repport_confirm_delete_subtitle": "زیرعنوان \"{{subtitle}}\" و همه سوالات آن حذف شود؟",
      "repport_confirm_delete_question": "این سوال حذف شود؟",
      "repport_err_app_missing": "برنامه مشخص نیست (localStorage).",
      "repport_err_title_required": "عنوان الزامی است.",
      "repport_err_title_exists": "این عنوان برای این برنامه از قبل وجود دارد.",
      "repport_err_title_add_failed": "افزودن عنوان ناموفق بود.",
      "repport_err_title_delete_failed": "حذف عنوان ناموفق بود.",
      "repport_err_select_title_first": "ابتدا یک عنوان انتخاب کنید.",
      "repport_err_subtitle_required": "زیرعنوان الزامی است.",
      "repport_err_subtitle_exists": "این زیرعنوان از قبل وجود دارد.",
      "repport_err_subtitle_add_failed": "افزودن زیرعنوان ناموفق بود.",
      "repport_err_subtitle_delete_failed": "حذف زیرعنوان ناموفق بود.",
      "repport_err_select_title_subtitle_first": "ابتدا عنوان و زیرعنوان را انتخاب کنید.",
      "repport_err_question_text_required": "متن سوال الزامی است.",
      "repport_err_invalid_question_type": "نوع سوال نامعتبر است.",
      "repport_err_qcm_options_required": "برای چندگزینه‌ای، حداقل یک گزینه اضافه کنید.",
      "repport_question_added": "سوال اضافه شد.",
      "repport_err_question_add_failed": "افزودن سوال ناموفق بود.",
      "repport_err_question_delete_failed": "حذف سوال ناموفق بود.",
      "repport_title_added": "عنوان اضافه شد.",
      "repport_subtitle_added": "زیرعنوان اضافه شد.",
      "repport_err_titles_load": "بارگذاری عنوان‌ها ناموفق بود.",
      "repport_err_subtitles_load": "بارگذاری زیرعنوان‌ها ناموفق بود.",
      "repport_err_questions_load": "بارگذاری سوالات ناموفق بود.",
      "repport_err_history_load": "بارگذاری تاریخچه ناموفق بود.",
      "repport_err_build_report": "ساخت گزارش ناموفق بود (meta/questions).",
      "repport_err_pdf_download": "دانلود PDF در حال حاضر ممکن نیست.",
      "repport_history_app_required": "نام برنامه الزامی است.",
      "repport_history_success": "تاریخچه با موفقیت بارگذاری شد.",
      "repport_history_db_error": "خطای پایگاه داده هنگام بارگذاری تاریخچه.",
      "repport_meta_required_ids": "شناسه سوالات الزامی است.",
      "repport_meta_no_valid_ids": "هیچ شناسه معتبری ارائه نشده است.",
      "repport_meta_success": "اطلاعات متا با موفقیت بارگذاری شد.",
      "repport_meta_db_error": "خطای پایگاه داده هنگام بارگذاری اطلاعات متا.",
      "ur_app_required": "پارامتر اپلیکیشن الزامی است.",
      "ur_users_loaded": "کاربران بارگذاری شدند.",
      "ur_server_error": "خطای سرور. لطفاً بعداً دوباره تلاش کنید.",
      "ur_missing_fields": "فیلدهای ضروری ناقص هستند.",
      "ur_tech_fields_required": "تخصص و بخش برای تکنسین‌ها الزامی است.",
      "ur_email_exists": "این ایمیل قبلاً ثبت شده است.",
      "ur_username_exists": "این نام کاربری برای این اپلیکیشن قبلاً استفاده شده است.",
      "ur_register_success": "کاربر با موفقیت ثبت شد.",
      "ur_id_required": "فیلد ضروری موجود نیست: id.",
      "ur_user_not_found": "کاربر پیدا نشد.",
      "ur_delete_success": "کاربر با موفقیت حذف شد.",
      "na": "ناموجود",
      "unknown": "نامشخص",
      "client": "مشتری",
      "technician": "تکنسین",
      "type": "نوع",
      "required": "الزامی",
      "view": "مشاهده",
      "rp_qr": "کد پاسخ سریع",
      "no_data": "بدون داده",
      "signature": "امضا",
      "add": "افزودن",
      "admin_manage_apps_users": "مدیریت برنامه‌ها و کاربران",
      "admin_users_total": "مجموع کاربران",
      "application": "اپلیکیشن",
      "auth_login_story_text": "برای دسترسی به کدهای QR، گزارش‌ها و مداخلات وارد شوید.",
      "auth_login_story_title": "خوش آمدید",
      "auth_proof_access": "دسترسی امن",
      "auth_proof_qr": "دستگاه‌های متصل",
      "auth_proof_reports": "گزارش‌های خودکار",
      "auth_signup_feature_1": "مدیریت کدهای QR",
      "auth_signup_feature_2": "گزارش‌های هوشمند",
      "auth_signup_feature_3": "زمان‌بندی مداخلات",
      "auth_signup_story_text": "فضای کاری خود را بسازید و نگهداری را دیجیتال کنید.",
      "auth_signup_story_title": "شروع با AssistByScan",
      "darkMode": {
        "toggle": "Toggle dark mode"
      },
      "date": "تاریخ",
      "details": "جزئیات",
      "filter": "فیلتر",
      "forgot_intro": "ایمیل خود را وارد کنید تا لینک بازنشانی دریافت کنید.",
      "forgot_resend_window": "می‌توانید تا {{seconds}} ثانیه دیگر دوباره ارسال کنید.",
      "forgot_secure_link": "لینک امن به ایمیل شما ارسال شد.",
      "forgot_story_title": "بازنشانی رمز عبور",
      "help_replace_video": "جایگزینی ویدیو",
      "language": "زبان",
      "qr_generated_title": "کدهای QR تولیدشده",
      "reset_success": "رمز عبور با موفقیت بازنشانی شد.",
      "rp_popup_blocked": "پنجره بازشو مسدود شد. لطفا پاپ‌آپ را مجاز کنید.",
      "static_title": "صفحات ثابت",
      "theme_dark": "حالت تیره",
      "theme_light": "حالت روشن",
      "urw_add_other": "افزودن کاربر دیگر",
      "urw_subtitle": "برای اپلیکیشن خود کاربر ثبت کنید.",
      "urw_success_title": "کاربر با موفقیت ثبت شد"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "fr",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
  });

export default i18n;
